/**
 * MyReports.js — CL (Checklist) Reports + OTT (One-Time Task) Reports
 * Moved from Management Reports (Reports.js) to be a standalone screen.
 *
 * Features:
 *  - User Wise / Task Wise toggle for both tabs
 *  - 3D user cards with gradient headers, progress bars, star badges
 *  - Date range filter shared across views
 *  - Search bar to filter checklists/tasks by name
 *  - Select-all + bulk operations (approve / mark complete)
 *  - Export CSV
 */

import React, { useState, useEffect, useCallback } from "react";
import api from "./api";
import useAuth from "../hooks/useAuth";
import "./solution/Reports.css";

/* ── helpers ───────────────────────────────────────────────── */
function todayStr() { return new Date().toISOString().slice(0, 10); }
function yesterdayStr() {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}
function fmtShortDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtNum(n) { return (n ?? 0).toLocaleString("en-IN"); }

function exportCSV(rows, headers, filename) {
  const lines = [headers.join(","), ...rows.map(r => r.map(c => `"${c ?? ""}"`).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename + ".csv";
  a.click(); URL.revokeObjectURL(url);
}

const SUPER_ROLES = ["owner", "solution", "admin", "supervisor"];

const TABS = [
  { id: "checklists", label: "☑️ CL Reports" },
  { id: "ott",        label: "🗂️ OTT Reports" },
  { id: "planner",    label: "📅 Staff Planner" },
  { id: "attendance", label: "📊 Attendance" },
  { id: "revenue",    label: "💰 Revenue" },
  { id: "enrollments",label: "🆕 Enrollments" },
];

export default function MyReports() {
  const { auth } = useAuth();
  const roles = auth?.roles || [];
  const username = auth?.username || "";
  const isSuper = roles.some(r => SUPER_ROLES.includes(r));

  const [activeTab, setActiveTab] = useState("checklists");
  const [from, setFrom]   = useState(todayStr());
  const [to,   setTo]     = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  // CL state
  const [allChecklists, setAllChecklists] = useState([]);
  const [clFilterUser,  setClFilterUser]  = useState("__all__");
  const [clExpanded,    setClExpanded]    = useState({});
  const [clView,        setClView]        = useState("user");
  const [clSearch,      setClSearch]      = useState("");
  const [clSelected,    setClSelected]    = useState(new Set());
  const [clCardFilter,  setClCardFilter]  = useState(new Set(["completed", "missed"])); // multiselect filter

  // OTT state
  const [allTasks,      setAllTasks]      = useState([]);
  const [ottFilterUser, setOttFilterUser] = useState("__all__");
  const [ottExpanded,   setOttExpanded]   = useState({});
  const [ottView,       setOttView]       = useState("user");
  const [ottSearch,     setOttSearch]     = useState("");
  const [ottSelected,   setOttSelected]   = useState(new Set());

  // Planner state
  const [plannerData,       setPlannerData]       = useState({});
  const [plannerStaff,      setPlannerStaff]      = useState([]);
  const [plannerFilterUser, setPlannerFilterUser] = useState("__all__");

  // Attendance state
  const [attPatientSessions, setAttPatientSessions] = useState([]);
  const [attStaffRecords,    setAttStaffRecords]    = useState([]);
  const [attAllStaff,        setAttAllStaff]        = useState([]); // all session staff (to detect absentees)
  const [missedFilter,       setMissedFilter]       = useState('all'); // 'all' | 'with' | 'without'
  const [missedExpanded,     setMissedExpanded]     = useState({}); // { patientCard: true }

  // Revenue state
  const [revBillings,   setRevBillings]   = useState([]);
  const [revAppts,      setRevAppts]      = useState([]);

  // Enrollments state
  const [enrollPatients, setEnrollPatients] = useState([]);

  /* ── fetch ────────────────────────────────────────────── */
  const fetchTab = useCallback(async (tab) => {
    setLoading(true); setError("");
    try {
      if (tab === "checklists") {
        const r = await api.get(
          `${process.env.REACT_APP_URL}/checklists?from=${from}&to=${to}`
        ).catch(() => ({ data: [] }));
        setAllChecklists(Array.isArray(r.data) ? r.data : []);
      } else if (tab === "ott") {
        const r = await api.get(
          `${process.env.REACT_APP_URL}/tasks`
        ).catch(() => ({ data: [] }));
        setAllTasks(Array.isArray(r.data) ? r.data : []);
      } else if (tab === "planner") {
        // Fetch staff list + attendance + appointments for each staff
        const staffRes = await api.get(`${process.env.REACT_APP_URL}/users/sessionstaff/all`).catch(() => ({ data: [] }));
        const staff = Array.isArray(staffRes.data) ? staffRes.data : [];
        setPlannerStaff(staff);

        const attRes = await api.get(`${process.env.REACT_APP_URL}/myattendance/report?from=${from}&to=${to}`).catch(() => ({ data: { records: [] } }));
        const attRecords = attRes.data?.records || [];

        // Build data map: { username: { "YYYY-MM-DD": { attendance, appointments: [] } } }
        const dataMap = {};
        // Index attendance by username+date
        attRecords.forEach(rec => {
          const u = rec.username;
          const d = rec.date; // "YYYY-MM-DD"
          if (!dataMap[u]) dataMap[u] = {};
          dataMap[u][d] = { attendance: rec, appointments: [] };
        });

        // IST date helper — avoids UTC→IST day shift
        const toISTDate = (d) => {
          const dt = new Date(d);
          return new Date(dt.getTime() + 330 * 60000).toISOString().slice(0, 10);
        };

        // Fetch ALL appointments in date range (single bulk call instead of N+1)
        const allApptRes = await api.get(`${process.env.REACT_APP_URL}/appointments?from=${from}&to=${to}`).catch(() => ({ data: [] }));
        const allAppts = Array.isArray(allApptRes.data) ? allApptRes.data : [];
        const staffSet = new Set(staff.map(s => s.username));

        allAppts.forEach(apt => {
          const d = apt.date ? toISTDate(apt.date) : null;
          if (!d) return;

          // Map to lead therapist (username field)
          const lead = apt.username;
          if (lead && staffSet.has(lead)) {
            if (!dataMap[lead]) dataMap[lead] = {};
            if (!dataMap[lead][d]) dataMap[lead][d] = { attendance: null, appointments: [] };
            if (!dataMap[lead][d].appointments.find(a => a._id === apt._id)) {
              dataMap[lead][d].appointments.push(apt);
            }
          }

          // Map to helper therapists
          [apt.helperTherapist1, apt.helperTherapist2].filter(Boolean).forEach(helper => {
            if (!dataMap[helper]) dataMap[helper] = {};
            if (!dataMap[helper][d]) dataMap[helper][d] = { attendance: null, appointments: [] };
            if (!dataMap[helper][d].appointments.find(a => a._id === apt._id)) {
              dataMap[helper][d].appointments.push({ ...apt, _isHelper: true });
            }
          });
        });

        setPlannerData(dataMap);
      } else if (tab === "attendance") {
        // Patient sessions (Therapy appointments) + Staff attendance + all staff list
        var [apptRes, staffRes, allStaffRes] = await Promise.all([
          api.get(process.env.REACT_APP_URL + "/appointments?from=" + from + "&to=" + to).catch(function() { return { data: [] }; }),
          api.get(process.env.REACT_APP_URL + "/myattendance/report?from=" + from + "&to=" + to).catch(function() { return { data: { records: [] } }; }),
          api.get(process.env.REACT_APP_URL + "/users/sessionstaff/all").catch(function() { return { data: [] }; }),
        ]);
        setAttPatientSessions(Array.isArray(apptRes.data) ? apptRes.data.filter(function(a) { return a.type === "Therapy"; }) : []);
        setAttStaffRecords(staffRes.data && staffRes.data.records ? staffRes.data.records : []);
        setAttAllStaff(Array.isArray(allStaffRes.data) ? allStaffRes.data : []);
      } else if (tab === "revenue") {
        // Billings + all therapy appointments for charity/charges
        const [billRes, apptRes] = await Promise.all([
          api.get(`${process.env.REACT_APP_URL}/bills/by-date?from=${from}&to=${to}`).catch(() => ({ data: [] })),
          api.get(`${process.env.REACT_APP_URL}/appointments?from=${from}&to=${to}`).catch(() => ({ data: [] })),
        ]);
        setRevBillings(Array.isArray(billRes.data) ? billRes.data : []);
        setRevAppts(Array.isArray(apptRes.data) ? apptRes.data.filter(a => a.type === "Therapy") : []);
      } else if (tab === "enrollments") {
        // All patients — filter by registrationDate on frontend
        const r = await api.get(`${process.env.REACT_APP_URL}/patients/profiles/registered`).catch(() => ({ data: [] }));
        const all = Array.isArray(r.data) ? r.data : [];
        const fromD = new Date(from); fromD.setHours(0,0,0,0);
        const toD   = new Date(to);   toD.setHours(23,59,59,999);
        setEnrollPatients(all.filter(p => {
          const rd = p.registrationDate ? new Date(p.registrationDate) : null;
          return rd && rd >= fromD && rd <= toD;
        }));
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchTab(activeTab); }, [activeTab, fetchTab]);

  // Reset selections on tab/data change
  useEffect(() => { setClSelected(new Set()); }, [allChecklists]);
  useEffect(() => { setOttSelected(new Set()); }, [allTasks]);

  /* ── bulk helpers ───────────────────────────────────── */
  async function bulkCompleteCL(ids) {
    if (!ids.length) return;
    const today = new Date().toISOString().slice(0, 10);
    let success = 0;
    for (const id of ids) {
      try {
        await api.patch(`${process.env.REACT_APP_URL}/checklists/${id}/complete`, {
          date: today, markedBy: username,
        });
        success++;
      } catch (e) { /* skip already completed */ }
    }
    alert(`Marked ${success}/${ids.length} checklists as completed for today.`);
    setClSelected(new Set());
    fetchTab("checklists");
  }

  async function bulkUpdateTasks(taskIds, updates) {
    if (!taskIds.length) return;
    try {
      const tasks = taskIds.map(id => ({ _id: id, ...updates }));
      await api.post(`${process.env.REACT_APP_URL}/tasks/bulk-update`, {
        tasks, user: { username, roles },
      });
      alert(`Updated ${taskIds.length} tasks.`);
      setOttSelected(new Set());
      fetchTab("ott");
    } catch (e) {
      alert("Bulk update failed: " + (e.response?.data?.message || e.message));
    }
  }

  /* ═══════════════════════════════════════════════════════
   *  CL Reports Renderer
   * ═══════════════════════════════════════════════════════ */
  function renderChecklists() {
    // Use IST-anchored dates to avoid UTC off-by-one
    const fromD = new Date(from + "T00:00:00+05:30");
    const toD   = new Date(to   + "T23:59:59.999+05:30");
    const searchLower = clSearch.toLowerCase();

    const userStats = {};
    const taskStats = {};

    allChecklists.forEach(cl => {
      const title = cl.title || "Untitled";
      if (searchLower && !title.toLowerCase().includes(searchLower)) return;

      const user = cl.assignee || "Unassigned";
      if (!isSuper && user !== username) return; // non-super users only see their own

      if (!userStats[user]) userStats[user] = { total: 0, completed: 0, missed: 0, na: 0, late: 0, greenStars: 0, redStars: 0, items: [] };

      if (!taskStats[title]) taskStats[title] = { total: 0, completed: 0, missed: 0, na: 0, late: 0, users: {}, items: [], frequency: cl.frequency };

      const recurrences = (cl.recurrences || []).filter(r => {
        const rd = new Date(r.date || r);
        return rd >= fromD && rd <= toD;
      });
      // Only count stars if this checklist has recurrences in the date range
      if (recurrences.length > 0) {
        userStats[user].greenStars += (cl.merit?.greenStars || 0);
        userStats[user].redStars   += (cl.merit?.redStars || 0);
      }

      const completionDates = new Set((cl.completions || []).map(c => new Date(c.date).toDateString()));
      const naDates = new Set((cl.completions || []).filter(c => c.notApplicable).map(c => new Date(c.date).toDateString()));
      const lateDates = new Set((cl.completions || []).filter(c => c.isLate).map(c => new Date(c.date).toDateString()));

      recurrences.forEach(r => {
        const rd = new Date(r.date || r);
        const rdStr = rd.toDateString();
        const isDone = completionDates.has(rdStr);
        const isNA   = naDates.has(rdStr);
        const isLate = lateDates.has(rdStr);
        const isPast = rd < new Date();

        const item = { clId: cl._id, title, description: cl.description, date: rd, isDone, isNA, isLate, isPast, frequency: cl.frequency, user };

        userStats[user].total++;
        if (isNA) userStats[user].na++;
        else if (isDone) { userStats[user].completed++; if (isLate) userStats[user].late++; }
        else if (isPast) userStats[user].missed++;
        userStats[user].items.push(item);

        taskStats[title].total++;
        if (isNA) taskStats[title].na++;
        else if (isDone) { taskStats[title].completed++; if (isLate) taskStats[title].late++; }
        else if (isPast) taskStats[title].missed++;
        if (!taskStats[title].users[user]) taskStats[title].users[user] = { done: 0, missed: 0 };
        if (isDone && !isNA) taskStats[title].users[user].done++;
        else if (isPast && !isNA && !isDone) taskStats[title].users[user].missed++;
        taskStats[title].items.push(item);
      });
    });

    const users = Object.keys(userStats).sort();
    const tasks = Object.keys(taskStats).sort();
    const filteredUsers = clFilterUser === "__all__" ? users : users.filter(u => u === clFilterUser);

    let gTotal = 0, gDone = 0, gMissed = 0, gNA = 0, gLate = 0, gGreen = 0, gRed = 0;
    filteredUsers.forEach(u => {
      const s = userStats[u];
      gTotal += s.total; gDone += s.completed; gMissed += s.missed;
      gNA += s.na; gLate += s.late; gGreen += s.greenStars; gRed += s.redStars;
    });

    // Collect selectable IDs based on active card filter (missed items only — for bulk complete)
    const allMissedIds = [];
    filteredUsers.forEach(u => {
      userStats[u].items.filter(i => !i.isDone && !i.isNA && i.isPast).forEach(i => {
        if (!allMissedIds.includes(i.clId)) allMissedIds.push(i.clId);
      });
    });

    const allSelected = allMissedIds.length > 0 && allMissedIds.every(id => clSelected.has(id));

    // Helper to toggle card filter
    const toggleCardFilter = (key) => {
      setClCardFilter(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
    };

    return (
      <div>
        {/* Controls row */}
        <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 2, background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
            <button style={toggleBtnStyle(clView === "user")} onClick={() => setClView("user")}>User Wise</button>
            <button style={toggleBtnStyle(clView === "task")} onClick={() => setClView("task")}>Task Wise</button>
          </div>
          {isSuper && (
            <select value={clFilterUser} onChange={e => setClFilterUser(e.target.value)}
              style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}>
              <option value="__all__">All Users</option>
              {users.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          )}
          <input
            type="text" placeholder="Search checklists..." value={clSearch}
            onChange={e => setClSearch(e.target.value)}
            style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, minWidth: 160 }}
          />
          <button className="rpt-export-btn" onClick={() => exportCSV(
            filteredUsers.map(u => { const s = userStats[u]; return [u, s.total, s.completed, s.missed, s.late, s.na, s.greenStars, s.redStars]; }),
            ["User", "Total", "Completed", "Missed", "Late", "N/A", "Green Stars", "Red Stars"],
            `checklist_report_${from}_${to}`
          )}>⬇ Export CSV</button>
        </div>

        {/* Card filter badges + Select all + bulk actions */}
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f1f5f9", borderRadius: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>Show:</span>
          {gDone > 0 && (
            <button onClick={() => toggleCardFilter("completed")} style={{
              padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
              border: clCardFilter.has("completed") ? "2px solid #16a34a" : "1.5px solid #cbd5e1",
              background: clCardFilter.has("completed") ? "#dcfce7" : "#fff",
              color: clCardFilter.has("completed") ? "#16a34a" : "#94a3b8",
            }}>
              Completed ({fmtNum(gDone)})
            </button>
          )}
          {gMissed > 0 && (
            <button onClick={() => toggleCardFilter("missed")} style={{
              padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
              border: clCardFilter.has("missed") ? "2px solid #dc2626" : "1.5px solid #cbd5e1",
              background: clCardFilter.has("missed") ? "#fef2f2" : "#fff",
              color: clCardFilter.has("missed") ? "#dc2626" : "#94a3b8",
            }}>
              Missed ({fmtNum(gMissed)})
            </button>
          )}
          {isSuper && allMissedIds.length > 0 && (
            <>
              <span style={{ width: 1, height: 20, background: "#cbd5e1" }} />
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                <input type="checkbox" checked={allSelected} onChange={() => {
                  if (allSelected) setClSelected(new Set());
                  else setClSelected(new Set(allMissedIds));
                }} />
                Select All Missed ({allMissedIds.length})
              </label>
              {clSelected.size > 0 && (
                <button onClick={() => bulkCompleteCL([...clSelected])} style={{
                  padding: "5px 14px", borderRadius: 8, border: "none", background: "#16a34a", color: "#fff",
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}>
                  Mark {clSelected.size} as Completed
                </button>
              )}
            </>
          )}
        </div>

        <div className="rpt-summary-row">
          <Kpi label="Total Recurrences" value={fmtNum(gTotal)} />
          <Kpi label="Completed" value={fmtNum(gDone)} color="#388e3c" />
          <Kpi label="Missed" value={fmtNum(gMissed)} color="#e53935" />
          <Kpi label="Late Done" value={fmtNum(gLate)} color="#f57c00" />
          <Kpi label="N/A" value={fmtNum(gNA)} color="#9e9e9e" />
          <Kpi label="Green Stars" value={fmtNum(gGreen)} color="#388e3c" />
          <Kpi label="Red Stars" value={fmtNum(gRed)} color="#e53935" />
        </div>

        {/* ═══ USER WISE — 3D cards (always expanded) ═══ */}
        {clView === "user" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16, marginTop: 8 }}>
            {filteredUsers.map(u => {
              const s = userStats[u];
              const pct = s.total > 0 ? Math.round(s.completed / s.total * 100) : 0;
              const completed = clCardFilter.has("completed") ? s.items.filter(i => i.isDone && !i.isNA).sort((a,b) => b.date - a.date) : [];
              const missed    = clCardFilter.has("missed") ? s.items.filter(i => !i.isDone && !i.isNA && i.isPast).sort((a,b) => b.date - a.date) : [];

              return (
                <div key={u} style={{
                  background: "#fff", borderRadius: 18, overflow: "hidden",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
                  border: "1px solid #e2e8f0",
                }}>
                  <div style={{
                      padding: "16px 18px",
                      background: s.missed > 0
                        ? "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)"
                        : pct === 100
                          ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                          : "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                      borderBottom: "1px solid #e2e8f0",
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>{u}</div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          <span>{s.total} recurrences</span>
                          {s.completed > 0 && <span>&middot; <span style={{ color: "#16a34a", fontWeight: 700 }}>{s.completed} done</span></span>}
                          {s.missed > 0 && <span>&middot; <span style={{ color: "#dc2626", fontWeight: 700 }}>{s.missed} missed</span></span>}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{
                          fontSize: 22, fontWeight: 900, lineHeight: 1,
                          color: pct === 100 ? "#16a34a" : pct >= 70 ? "#1976d2" : "#dc2626",
                        }}>{pct}%</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, background: "#e2e8f0", borderRadius: 4, height: 6, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 4, width: `${pct}%`, transition: "width 0.3s",
                        background: pct === 100 ? "#16a34a" : pct >= 70 ? "#3b82f6" : "#ef4444" }} />
                    </div>
                    {(s.greenStars > 0 || s.redStars > 0) && (
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        {s.greenStars > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: "#15803d", background: "#dcfce7", padding: "2px 8px", borderRadius: 6 }}>+{s.greenStars} green</span>}
                        {s.redStars > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", background: "#fef2f2", padding: "2px 8px", borderRadius: 6 }}>-{s.redStars} red</span>}
                      </div>
                    )}
                  </div>

                  {/* Always expanded — filtered by clCardFilter */}
                  <div style={{ padding: "12px 16px" }}>
                    {completed.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "#388e3c", marginBottom: 6 }}>Completed ({completed.length})</div>
                        {completed.slice(0, 20).map((item, idx) => (
                          <div key={idx} style={{
                            background: "linear-gradient(135deg,#dcfce7,#bbf7d0)", borderRadius: 8, padding: "7px 12px",
                            marginBottom: 4, borderLeft: "4px solid #16a34a", display: "flex", justifyContent: "space-between", alignItems: "center",
                          }}>
                            <div>
                              <span style={{ fontWeight: 600, fontSize: 12, color: "#15803d" }}>{item.title}</span>
                              {item.isLate && <span style={{ marginLeft: 6, fontSize: 9, background: "#fef3c7", color: "#92400e", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>LATE</span>}
                            </div>
                            <span style={{ fontSize: 11, color: "#15803d", whiteSpace: "nowrap" }}>{fmtShortDate(item.date)}</span>
                          </div>
                        ))}
                        {completed.length > 20 && <div style={{ fontSize: 11, color: "#94a3b8", padding: "4px 0" }}>+{completed.length - 20} more...</div>}
                      </div>
                    )}
                    {missed.length > 0 && (
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "#e53935", marginBottom: 6 }}>Missed ({missed.length})</div>
                        {missed.slice(0, 20).map((item, idx) => {
                          const daysAgo = Math.ceil((new Date() - item.date) / 86400000);
                          const sel = clSelected.has(item.clId);
                          return (
                            <div key={idx} style={{
                              background: sel ? "#fde68a" : "#fef2f2", borderRadius: 8, padding: "7px 12px",
                              marginBottom: 4, borderLeft: `4px solid ${sel ? "#d97706" : "#ef4444"}`, display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {isSuper && (
                                  <input type="checkbox" checked={sel} onChange={() => {
                                    setClSelected(prev => {
                                      const next = new Set(prev);
                                      if (next.has(item.clId)) next.delete(item.clId); else next.add(item.clId);
                                      return next;
                                    });
                                  }} />
                                )}
                                <span style={{ fontWeight: 600, fontSize: 12, color: "#991b1b" }}>{item.title}</span>
                              </div>
                              <span style={{ fontSize: 11, color: "#dc2626", whiteSpace: "nowrap" }}>{fmtShortDate(item.date)} ({daysAgo}d)</span>
                            </div>
                          );
                        })}
                        {missed.length > 20 && <div style={{ fontSize: 11, color: "#94a3b8", padding: "4px 0" }}>+{missed.length - 20} more...</div>}
                      </div>
                    )}
                    {completed.length === 0 && missed.length === 0 && (
                      <div style={{ fontSize: 12, color: "#94a3b8", padding: 4 }}>No activity in this period</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ TASK WISE — table ═══ */}
        {clView === "task" && (() => {
          // Build calendar days array for the selected date range (IST-anchored)
          const calStart = new Date(from + "T00:00:00+05:30");
          const calYear  = calStart.getFullYear();
          const calMonth = calStart.getMonth();
          const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
          const monthLabel = calStart.toLocaleDateString("en-IN", { month: "long", year: "numeric" });

          // Filtered tasks based on clCardFilter (completed / missed)
          const showCompleted = clCardFilter.has("completed");
          const showMissed    = clCardFilter.has("missed");

          // Pre-compute all task rows data
          const taskRows = tasks.map(title => {
            const ts = taskStats[title];
            let items = clFilterUser === "__all__" ? ts.items : ts.items.filter(i => i.user === clFilterUser);
            if (!showCompleted || !showMissed) {
              items = items.filter(i => {
                if (i.isDone && !i.isNA) return showCompleted;
                if (!i.isDone && !i.isNA && i.isPast) return showMissed;
                return true;
              });
            }
            if (items.length === 0) return null;
            const dayMap = {};
            items.forEach(item => { const d = item.date.getDate(); if (!dayMap[d]) dayMap[d] = []; dayMap[d].push(item); });
            const done = items.filter(i => i.isDone && !i.isNA).length;
            const miss = items.filter(i => !i.isDone && !i.isNA && i.isPast).length;
            const missedIds = items.filter(i => !i.isDone && !i.isNA && i.isPast).map(i => i.clId).filter((v, idx, a) => a.indexOf(v) === idx);
            return { title, ts, dayMap, done, miss, missedIds };
          }).filter(Boolean);

          return (
          <div className="rpt-table-wrap">
            <div className="rpt-table-toolbar">
              <span className="rpt-table-title">Task Calendar — {monthLabel}</span>
            </div>

            {/* Excel-like grid: scrollable horizontally */}
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "140px repeat(" + daysInMonth + ", 36px)",
                gap: "3px 3px",
                minWidth: "fit-content",
              }}>
                {/* ── Header row: blank + day numbers ── */}
                <div style={{ position: "sticky", left: 0, zIndex: 2, background: "#f1f5f9", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#64748b", padding: "4px 6px" }}>
                  Task / Day
                </div>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                  const cellDate = new Date(calYear, calMonth, day);
                  const dayName = cellDate.toLocaleDateString("en-IN", { weekday: "narrow" });
                  const isToday = cellDate.toISOString().slice(0,10) === todayStr();
                  return (
                    <div key={day} style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      fontSize: 9, fontWeight: 700, color: isToday ? "#6366f1" : "#94a3b8",
                      background: isToday ? "#e0e7ff" : "#f8fafc", borderRadius: 6, padding: "2px 0",
                      border: isToday ? "1.5px solid #818cf8" : "1px solid #e2e8f0",
                    }}>
                      <span>{dayName}</span>
                      <span style={{ fontSize: 11, fontWeight: 800 }}>{day}</span>
                    </div>
                  );
                })}

                {/* ── Task rows ── */}
                {taskRows.map(row => {
                  const { title, ts, dayMap, done, miss, missedIds } = row;
                  return (
                    <React.Fragment key={title}>
                      {/* Task name cell — sticky left */}
                      <div style={{
                        position: "sticky", left: 0, zIndex: 2,
                        background: "#f8fafc", borderRadius: 8,
                        border: "1px solid #e2e8f0", padding: "4px 8px",
                        display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 36,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {isSuper && missedIds.length > 0 && (
                            <input type="checkbox"
                              checked={missedIds.every(id => clSelected.has(id))}
                              onChange={() => {
                                setClSelected(prev => {
                                  const next = new Set(prev);
                                  const allSel = missedIds.every(id => next.has(id));
                                  missedIds.forEach(id => allSel ? next.delete(id) : next.add(id));
                                  return next;
                                });
                              }}
                              title="Select all missed"
                              style={{ width: 13, height: 13, cursor: "pointer", flexShrink: 0 }}
                            />
                          )}
                          <span style={{ fontWeight: 700, fontSize: 11, color: "#0f172a", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
                        </div>
                        <div style={{ fontSize: 9, color: "#64748b", marginTop: 1 }}>
                          {ts.frequency || ""}
                          {done > 0 ? " " : ""}{done > 0 && <span style={{ color: "#16a34a", fontWeight: 700 }}>{done}✓</span>}
                          {miss > 0 ? " " : ""}{miss > 0 && <span style={{ color: "#dc2626", fontWeight: 700 }}>{miss}✗</span>}
                        </div>
                      </div>

                      {/* Day cells for this task */}
                      {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                        const dayItems = dayMap[day] || [];
                        const today = new Date(); today.setHours(0,0,0,0);
                        const cellDate = new Date(calYear, calMonth, day);
                        const isFuture = cellDate > today;

                        let bg = "#f8fafc", icon = "", border = "1px solid #e2e8f0", cursor = "default", textColor = "#94a3b8";

                        if (dayItems.length === 0) {
                          icon = "—"; bg = isFuture ? "#f1f5f9" : "#eff6ff"; textColor = "#93c5fd";
                        } else {
                          const allDone = dayItems.every(i => i.isDone || i.isNA);
                          const anyMissed = dayItems.some(i => !i.isDone && !i.isNA && i.isPast);
                          const anyLate = dayItems.some(i => i.isLate && i.isDone);
                          cursor = "pointer";
                          if (allDone) {
                            icon = "✓"; bg = anyLate ? "#fef3c7" : "#dcfce7";
                            border = anyLate ? "1.5px solid #fbbf24" : "1.5px solid #86efac";
                            textColor = anyLate ? "#d97706" : "#16a34a";
                          } else if (anyMissed) {
                            icon = "✗"; bg = "#fee2e2"; border = "1.5px solid #fca5a5"; textColor = "#dc2626";
                          } else if (isFuture) {
                            icon = "·"; bg = "#f5f3ff"; border = "1px solid #e9d5ff"; textColor = "#a78bfa";
                          }
                        }

                        return (
                          <div key={day}
                            onClick={() => {
                              if (dayItems.length > 0) {
                                setClExpanded({ __clModal: { title, day, items: dayItems, date: new Date(calYear, calMonth, day) } });
                              }
                            }}
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: bg, border, borderRadius: 6, cursor, height: 36,
                            }}
                            title={dayItems.length > 0 ? dayItems.map(i => i.user + ': ' + (i.isDone ? 'Done' : i.isNA ? 'N/A' : i.isPast ? 'Missed' : 'Pending')).join('\n') : 'No task'}
                          >
                            <span style={{ fontSize: 13, fontWeight: 800, color: textColor }}>{icon}</span>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* ── Modal popup for clicked day cell ── */}
            {clExpanded.__clModal && (() => {
              const md = clExpanded.__clModal;
              return (
                <div onClick={() => setClExpanded({})} style={{
                  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                  background: "rgba(0,0,0,0.25)", zIndex: 9999,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <div onClick={e => e.stopPropagation()} style={{
                    width: 320, maxWidth: "90vw", maxHeight: "70vh", overflow: "auto",
                    padding: "16px 18px 16px 16px",
                    background: "linear-gradient(145deg, #f5f3ff 0%, #ede9fe 40%, #ddd6fe 100%)",
                    borderRadius: "6px 6px 6px 22px",
                    boxShadow: "3px 4px 20px rgba(0,0,0,0.18), inset 0 0 30px rgba(255,255,255,0.3)",
                    transform: "rotate(-0.5deg)", position: "relative",
                  }}>
                    {/* Folded corner */}
                    <div style={{ position: "absolute", bottom: 0, right: 0, width: 28, height: 28, background: "linear-gradient(135deg, #ede9fe 50%, #b4a7d6 50%)", borderRadius: "12px 0 0 0" }} />
                    {/* Tape strip */}
                    <div style={{ position: "absolute", top: -6, left: "50%", transform: "translateX(-50%) rotate(1deg)", width: 50, height: 14, background: "rgba(180,200,220,0.4)", borderRadius: 2 }} />
                    <button onClick={() => setClExpanded({})} style={{ position: "absolute", top: 8, right: 12, background: "none", border: "none", fontSize: 15, cursor: "pointer", color: "#7c3aed", fontWeight: 700 }}>✕</button>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#5b21b6", marginBottom: 8 }}>
                      {md.date.toLocaleDateString("en-IN", { day: "numeric", month: "short", weekday: "short" })}
                      <span style={{ fontWeight: 500, fontSize: 11, color: "#7c3aed", marginLeft: 8 }}>{md.title}</span>
                    </div>
                    {md.items.map((item, idx) => {
                      const statusLabel = item.isNA ? "N/A" : item.isDone ? (item.isLate ? "Done (Late)" : "Done") : item.isPast ? "Missed" : "Pending";
                      const statusColor = item.isNA ? "#9ca3af" : item.isDone ? (item.isLate ? "#d97706" : "#16a34a") : item.isPast ? "#dc2626" : "#3b82f6";
                      const isMissedItem = !item.isDone && !item.isNA && item.isPast;
                      return (
                        <div key={idx} style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "5px 0", borderBottom: idx < md.items.length - 1 ? "1px dashed #c4b5fd" : "none",
                        }}>
                          {isSuper && isMissedItem && (
                            <input type="checkbox" checked={clSelected.has(item.clId)} onChange={() => {
                              setClSelected(prev => {
                                const next = new Set(prev);
                                if (next.has(item.clId)) next.delete(item.clId); else next.add(item.clId);
                                return next;
                              });
                            }} style={{ width: 14, height: 14 }} />
                          )}
                          <span style={{ fontWeight: 600, fontSize: 12, color: "#374151", flex: 1 }}>{item.user}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: statusColor,
                            background: item.isDone ? "rgba(22,163,74,0.12)" : isMissedItem ? "rgba(220,38,38,0.10)" : "rgba(0,0,0,0.05)",
                            padding: "2px 8px", borderRadius: 6,
                          }}>{statusLabel}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Legend */}
            <div style={{ display: "flex", gap: 14, marginTop: 12, fontSize: 11, color: "#64748b", flexWrap: "wrap" }}>
              <span><span style={{ color: "#16a34a", fontWeight: 700 }}>✓</span> Done</span>
              <span><span style={{ color: "#dc2626", fontWeight: 700 }}>✗</span> Missed</span>
              <span><span style={{ color: "#93c5fd", fontWeight: 700 }}>—</span> Not applicable</span>
              <span><span style={{ color: "#d97706", fontWeight: 700 }}>✓</span> Done (Late)</span>
              <span><span style={{ color: "#a78bfa", fontWeight: 700 }}>·</span> Upcoming</span>
            </div>
          </div>
          );
        })()}

        {filteredUsers.length === 0 && <div className="rpt-empty">No checklist data for this period</div>}
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════
   *  OTT Reports Renderer — same User Wise / Task Wise toggle
   * ═══════════════════════════════════════════════════════ */
  function renderOTT() {
    const searchLower = ottSearch.toLowerCase();
    // IST-anchored date range for OTT filtering
    const ottFromD = new Date(from + "T00:00:00+05:30");
    const ottToD   = new Date(to   + "T23:59:59.999+05:30");

    const userStats = {};
    const taskStats = {};

    allTasks.forEach(t => {
      // Filter tasks by date range: use estimatedEndDate, or createdAt as fallback
      const taskDate = t.estimatedEndDate ? new Date(t.estimatedEndDate) : (t.createdAt ? new Date(t.createdAt) : null);
      if (taskDate && (taskDate < ottFromD || taskDate > ottToD)) return;

      const title = t.title || t.name || "Untitled";
      if (searchLower && !title.toLowerCase().includes(searchLower)) return;

      const user = t.assignee || "Unassigned";
      if (!isSuper && user !== username) return;

      if (!userStats[user]) userStats[user] = { total: 0, completed: 0, pending: 0, delayed: 0, overdue: 0, greenStars: 0, redStars: 0, tasks: [] };
      userStats[user].total++;
      userStats[user].greenStars += (t.merit?.greenStars || t.merit?.blueStars || 0);
      userStats[user].redStars   += (t.merit?.redStars || 0);

      const status = (t.status || "").toLowerCase();
      if (status === "completed") userStats[user].completed++;
      else if (status === "delayed") userStats[user].delayed++;
      else userStats[user].pending++;

      if (status !== "completed" && t.estimatedEndDate) {
        const diff = Math.ceil((new Date() - new Date(t.estimatedEndDate)) / 86400000);
        if (diff > 0) userStats[user].overdue++;
      }

      userStats[user].tasks.push(t);

      // Task stats (group by title)
      if (!taskStats[title]) taskStats[title] = { total: 0, completed: 0, pending: 0, delayed: 0, overdue: 0, users: {}, tasks: [], urgentLevel: t.urgentLevel };
      taskStats[title].total++;
      if (status === "completed") taskStats[title].completed++;
      else if (status === "delayed") taskStats[title].delayed++;
      else taskStats[title].pending++;
      if (status !== "completed" && t.estimatedEndDate) {
        const diff = Math.ceil((new Date() - new Date(t.estimatedEndDate)) / 86400000);
        if (diff > 0) taskStats[title].overdue++;
      }
      if (!taskStats[title].users[user]) taskStats[title].users[user] = { done: 0, pending: 0, delayed: 0 };
      if (status === "completed") taskStats[title].users[user].done++;
      else if (status === "delayed") taskStats[title].users[user].delayed++;
      else taskStats[title].users[user].pending++;
      taskStats[title].tasks.push(t);
    });

    const users = Object.keys(userStats).sort();
    const tasks = Object.keys(taskStats).sort();
    const filtered = ottFilterUser === "__all__" ? users : users.filter(u => u === ottFilterUser);

    let gTotal = 0, gDone = 0, gPending = 0, gDelayed = 0, gOverdue = 0, gGreen = 0, gRed = 0;
    filtered.forEach(u => {
      const s = userStats[u];
      gTotal += s.total; gDone += s.completed; gPending += s.pending;
      gDelayed += s.delayed; gOverdue += s.overdue; gGreen += s.greenStars; gRed += s.redStars;
    });

    // Collect all incomplete task IDs for select-all
    const allIncompleteIds = [];
    filtered.forEach(u => {
      userStats[u].tasks.filter(t => t.status?.toLowerCase() !== "completed").forEach(t => {
        if (t._id && !allIncompleteIds.includes(t._id)) allIncompleteIds.push(t._id);
      });
    });
    const allOttSelected = allIncompleteIds.length > 0 && allIncompleteIds.every(id => ottSelected.has(id));

    return (
      <div>
        {/* Controls row */}
        <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 2, background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
            <button style={toggleBtnStyle(ottView === "user")} onClick={() => setOttView("user")}>User Wise</button>
            <button style={toggleBtnStyle(ottView === "task")} onClick={() => setOttView("task")}>Task Wise</button>
          </div>
          {isSuper && (
            <select value={ottFilterUser} onChange={e => setOttFilterUser(e.target.value)}
              style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}>
              <option value="__all__">All Users</option>
              {users.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          )}
          <input
            type="text" placeholder="Search tasks..." value={ottSearch}
            onChange={e => setOttSearch(e.target.value)}
            style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, minWidth: 160 }}
          />
          <button className="rpt-export-btn" onClick={() => exportCSV(
            filtered.map(u => { const s = userStats[u]; return [u, s.total, s.completed, s.pending, s.delayed, s.overdue, s.greenStars, s.redStars]; }),
            ["User", "Total", "Completed", "Pending", "Delayed", "Overdue", "Green Stars", "Red Stars"],
            `ott_report_${todayStr()}`
          )}>⬇ Export CSV</button>
        </div>

        {/* Select all + bulk actions */}
        {isSuper && allIncompleteIds.length > 0 && (
          <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f1f5f9", borderRadius: 10, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              <input type="checkbox" checked={allOttSelected} onChange={() => {
                if (allOttSelected) setOttSelected(new Set());
                else setOttSelected(new Set(allIncompleteIds));
              }} />
              Select All Pending ({allIncompleteIds.length})
            </label>
            {ottSelected.size > 0 && (
              <>
                <button onClick={() => bulkUpdateTasks([...ottSelected], { status: "completed", finalEndDate: new Date().toISOString() })} style={{
                  padding: "5px 14px", borderRadius: 8, border: "none", background: "#16a34a", color: "#fff",
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}>
                  Mark {ottSelected.size} Completed
                </button>
                <button onClick={() => bulkUpdateTasks([...ottSelected], { status: "delayed" })} style={{
                  padding: "5px 14px", borderRadius: 8, border: "none", background: "#ef4444", color: "#fff",
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}>
                  Mark {ottSelected.size} Delayed
                </button>
              </>
            )}
          </div>
        )}

        <div className="rpt-summary-row">
          <Kpi label="Total Tasks" value={fmtNum(gTotal)} />
          <Kpi label="Completed" value={fmtNum(gDone)} color="#388e3c" />
          <Kpi label="Pending" value={fmtNum(gPending)} color="#f57c00" />
          <Kpi label="Delayed" value={fmtNum(gDelayed)} color="#e53935" />
          <Kpi label="Overdue" value={fmtNum(gOverdue)} color="#b71c1c" />
          <Kpi label="Green Stars" value={fmtNum(gGreen)} color="#388e3c" />
          <Kpi label="Red Stars" value={fmtNum(gRed)} color="#e53935" />
        </div>

        {/* ═══ USER WISE — 3D cards (same style as CL) ═══ */}
        {ottView === "user" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16, marginTop: 8 }}>
            {filtered.map(u => {
              const s = userStats[u];
              const pct = s.total > 0 ? Math.round(s.completed / s.total * 100) : 0;
              const isExpanded = ottExpanded[u];
              const completedTasks = s.tasks.filter(t => t.status?.toLowerCase() === "completed").sort((a,b) => new Date(b.finalEndDate || b.createdAt) - new Date(a.finalEndDate || a.createdAt));
              const pendingTasks   = s.tasks.filter(t => t.status?.toLowerCase() !== "completed").sort((a,b) => {
                const pri = x => x.status?.toLowerCase() === "delayed" ? 0 : 1;
                return pri(a) - pri(b) || new Date(a.estimatedEndDate || 0) - new Date(b.estimatedEndDate || 0);
              });

              return (
                <div key={u} style={{
                  background: "#fff", borderRadius: 18, overflow: "hidden",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
                  border: "1px solid #e2e8f0", transition: "transform 0.15s, box-shadow 0.15s",
                  transform: isExpanded ? "translateY(-2px)" : "none",
                }}>
                  <div onClick={() => setOttExpanded(prev => ({ ...prev, [u]: !prev[u] }))}
                    style={{
                      cursor: "pointer", padding: "16px 18px",
                      background: (s.delayed > 0 || s.overdue > 0)
                        ? "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)"
                        : pct === 100
                          ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                          : "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                      borderBottom: "1px solid #e2e8f0",
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>{u}</div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                          {s.total} tasks
                          {s.completed > 0 && <span> &middot; <span style={{ color: "#16a34a", fontWeight: 700 }}>{s.completed} done</span></span>}
                          {s.pending > 0 && <span> &middot; <span style={{ color: "#f59e0b", fontWeight: 600 }}>{s.pending} pending</span></span>}
                          {s.delayed > 0 && <span> &middot; <span style={{ color: "#dc2626", fontWeight: 700 }}>{s.delayed} delayed</span></span>}
                          {s.overdue > 0 && <span> &middot; <span style={{ color: "#b71c1c", fontWeight: 700 }}>{s.overdue} overdue</span></span>}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{
                          fontSize: 22, fontWeight: 900, lineHeight: 1,
                          color: pct === 100 ? "#16a34a" : pct >= 70 ? "#1976d2" : "#dc2626",
                        }}>{pct}%</div>
                        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{isExpanded ? "▲" : "▼"}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, background: "#e2e8f0", borderRadius: 4, height: 6, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 4, width: `${pct}%`, transition: "width 0.3s",
                        background: pct === 100 ? "#16a34a" : pct >= 70 ? "#3b82f6" : "#ef4444" }} />
                    </div>
                    {(s.greenStars > 0 || s.redStars > 0) && (
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        {s.greenStars > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: "#15803d", background: "#dcfce7", padding: "2px 8px", borderRadius: 6 }}>+{s.greenStars} green</span>}
                        {s.redStars > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", background: "#fef2f2", padding: "2px 8px", borderRadius: 6 }}>-{s.redStars} red</span>}
                      </div>
                    )}
                  </div>

                  {isExpanded && (
                    <div style={{ padding: "12px 16px" }}>
                      {/* Pending / Delayed */}
                      {pendingTasks.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontWeight: 700, fontSize: 12, color: "#e53935", marginBottom: 6 }}>Pending / Delayed ({pendingTasks.length})</div>
                          {pendingTasks.slice(0, 20).map((task, idx) => {
                            const isDelayed = task.status?.toLowerCase() === "delayed";
                            const overdueDays = task.estimatedEndDate && task.status !== "completed"
                              ? Math.max(0, Math.ceil((new Date() - new Date(task.estimatedEndDate)) / 86400000))
                              : 0;
                            const sel = ottSelected.has(task._id);
                            return (
                              <div key={task._id || idx} style={{
                                background: sel ? "#fde68a" : isDelayed ? "#fef2f2" : "#fffbeb", borderRadius: 8, padding: "7px 12px",
                                marginBottom: 4, borderLeft: `4px solid ${isDelayed ? "#ef4444" : "#f59e0b"}`,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  {isSuper && (
                                    <input type="checkbox" checked={sel} onChange={() => {
                                      setOttSelected(prev => {
                                        const next = new Set(prev);
                                        if (next.has(task._id)) next.delete(task._id); else next.add(task._id);
                                        return next;
                                      });
                                    }} />
                                  )}
                                  <div>
                                    <span style={{ fontWeight: 600, fontSize: 12, color: isDelayed ? "#991b1b" : "#92400e" }}>
                                      {task.title || task.name || "Untitled"}
                                    </span>
                                    {overdueDays > 0 && <span style={{ marginLeft: 6, fontSize: 9, background: "#fef2f2", color: "#dc2626", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>{overdueDays}d overdue</span>}
                                  </div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ fontSize: 9, fontWeight: 700, color: isDelayed ? "#dc2626" : "#92400e", background: isDelayed ? "#fee2e2" : "#fef3c7", padding: "2px 6px", borderRadius: 4, textTransform: "uppercase" }}>
                                    {task.status || "new"}
                                  </span>
                                  <span style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>
                                    {task.estimatedEndDate ? fmtShortDate(task.estimatedEndDate) : "—"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                          {pendingTasks.length > 20 && <div style={{ fontSize: 11, color: "#94a3b8", padding: "4px 0" }}>+{pendingTasks.length - 20} more...</div>}
                        </div>
                      )}

                      {/* Completed */}
                      {completedTasks.length > 0 && (
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 12, color: "#388e3c", marginBottom: 6 }}>Completed ({completedTasks.length})</div>
                          {completedTasks.slice(0, 20).map((task, idx) => (
                            <div key={task._id || idx} style={{
                              background: "linear-gradient(135deg,#dcfce7,#bbf7d0)", borderRadius: 8, padding: "7px 12px",
                              marginBottom: 4, borderLeft: "4px solid #16a34a", display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}>
                              <span style={{ fontWeight: 600, fontSize: 12, color: "#15803d" }}>{task.title || task.name || "Untitled"}</span>
                              <span style={{ fontSize: 11, color: "#15803d", whiteSpace: "nowrap" }}>{fmt(task.finalEndDate || task.updatedAt)}</span>
                            </div>
                          ))}
                          {completedTasks.length > 20 && <div style={{ fontSize: 11, color: "#94a3b8", padding: "4px 0" }}>+{completedTasks.length - 20} more...</div>}
                        </div>
                      )}

                      {pendingTasks.length === 0 && completedTasks.length === 0 && (
                        <div style={{ fontSize: 12, color: "#94a3b8", padding: 4 }}>No tasks assigned</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ TASK WISE — Calendar view (same as CL) ═══ */}
        {ottView === "task" && (() => {
          const now = new Date();
          const calMonth = now.getMonth();
          const calYear  = now.getFullYear();
          const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
          const monthLabel = now.toLocaleDateString("en-IN", { month: "long", year: "numeric" });

          // Pre-compute all task rows data
          const taskRows = tasks.map(title => {
            const ts = taskStats[title];
            const items = ottFilterUser === "__all__" ? ts.tasks : ts.tasks.filter(t => t.assignee === ottFilterUser);
            if (items.length === 0) return null;
            const dayMap = {};
            items.forEach(task => {
              const dd = task.estimatedEndDate ? new Date(task.estimatedEndDate) : (task.createdAt ? new Date(task.createdAt) : null);
              if (!dd || dd.getMonth() !== calMonth || dd.getFullYear() !== calYear) return;
              const d = dd.getDate();
              if (!dayMap[d]) dayMap[d] = [];
              dayMap[d].push(task);
            });
            const done = items.filter(t => t.status?.toLowerCase() === "completed").length;
            const pend = items.filter(t => !["completed","delayed"].includes(t.status?.toLowerCase())).length;
            const del  = items.filter(t => t.status?.toLowerCase() === "delayed").length;
            const incompleteIds = items.filter(t => t.status?.toLowerCase() !== "completed" && t._id).map(t => t._id).filter((v, i, a) => a.indexOf(v) === i);
            return { title, ts, dayMap, done, pend, del, incompleteIds };
          }).filter(Boolean);

          return (
          <div className="rpt-table-wrap">
            <div className="rpt-table-toolbar">
              <span className="rpt-table-title">Task Calendar — {monthLabel}</span>
            </div>

            {/* Excel-like grid: scrollable horizontally */}
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "140px repeat(" + daysInMonth + ", 36px)",
                gap: "3px 3px",
                minWidth: "fit-content",
              }}>
                {/* ── Header row: blank + day numbers ── */}
                <div style={{ position: "sticky", left: 0, zIndex: 2, background: "#f1f5f9", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#64748b", padding: "4px 6px" }}>
                  Task / Day
                </div>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                  const cellDate = new Date(calYear, calMonth, day);
                  const dayName = cellDate.toLocaleDateString("en-IN", { weekday: "narrow" });
                  const isToday = cellDate.toISOString().slice(0,10) === todayStr();
                  return (
                    <div key={day} style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      fontSize: 9, fontWeight: 700, color: isToday ? "#6366f1" : "#94a3b8",
                      background: isToday ? "#e0e7ff" : "#f8fafc", borderRadius: 6, padding: "2px 0",
                      border: isToday ? "1.5px solid #818cf8" : "1px solid #e2e8f0",
                    }}>
                      <span>{dayName}</span>
                      <span style={{ fontSize: 11, fontWeight: 800 }}>{day}</span>
                    </div>
                  );
                })}

                {/* ── Task rows ── */}
                {taskRows.map(row => {
                  const { title, ts, dayMap, done, pend, del, incompleteIds } = row;
                  return (
                    <React.Fragment key={title}>
                      {/* Task name cell — sticky left */}
                      <div style={{
                        position: "sticky", left: 0, zIndex: 2,
                        background: "#f8fafc", borderRadius: 8,
                        border: "1px solid #e2e8f0", padding: "4px 8px",
                        display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 36,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {isSuper && incompleteIds.length > 0 && (
                            <input type="checkbox"
                              checked={incompleteIds.every(id => ottSelected.has(id))}
                              onChange={() => {
                                setOttSelected(prev => {
                                  const next = new Set(prev);
                                  const allSel = incompleteIds.every(id => next.has(id));
                                  incompleteIds.forEach(id => allSel ? next.delete(id) : next.add(id));
                                  return next;
                                });
                              }}
                              title="Select all pending"
                              style={{ width: 13, height: 13, cursor: "pointer", flexShrink: 0 }}
                            />
                          )}
                          <span style={{ fontWeight: 700, fontSize: 11, color: "#0f172a", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
                        </div>
                        <div style={{ fontSize: 9, color: "#64748b", marginTop: 1 }}>
                          {ts.urgentLevel || ""}
                          {done > 0 ? " " : ""}{done > 0 && <span style={{ color: "#16a34a", fontWeight: 700 }}>{done}✓</span>}
                          {pend > 0 ? " " : ""}{pend > 0 && <span style={{ color: "#f59e0b", fontWeight: 700 }}>{pend}⏳</span>}
                          {del > 0 ? " " : ""}{del > 0 && <span style={{ color: "#dc2626", fontWeight: 700 }}>{del}✗</span>}
                        </div>
                      </div>

                      {/* Day cells for this task */}
                      {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                        const dayTasks = dayMap[day] || [];
                        const today = new Date(); today.setHours(0,0,0,0);
                        const cellDate = new Date(calYear, calMonth, day);
                        const isFuture = cellDate > today;

                        let bg = "#f8fafc", icon = "—", border = "1px solid #e2e8f0", cursor = "default", textColor = "#93c5fd";

                        if (dayTasks.length > 0) {
                          const allDone = dayTasks.every(t => t.status?.toLowerCase() === "completed");
                          const anyDelayed = dayTasks.some(t => t.status?.toLowerCase() === "delayed");
                          cursor = "pointer";
                          if (allDone) {
                            icon = "✓"; bg = "#dcfce7"; border = "1.5px solid #86efac"; textColor = "#16a34a";
                          } else if (anyDelayed) {
                            icon = "✗"; bg = "#fee2e2"; border = "1.5px solid #fca5a5"; textColor = "#dc2626";
                          } else if (isFuture) {
                            icon = "·"; bg = "#f5f3ff"; border = "1px solid #e9d5ff"; textColor = "#a78bfa";
                          } else {
                            icon = "!"; bg = "#fef3c7"; border = "1.5px solid #fbbf24"; textColor = "#d97706";
                          }
                        } else {
                          bg = isFuture ? "#f1f5f9" : "#eff6ff";
                        }

                        return (
                          <div key={day}
                            onClick={() => {
                              if (dayTasks.length > 0) {
                                setOttExpanded({ __ottModal: { title, day, tasks: dayTasks, date: new Date(calYear, calMonth, day) } });
                              }
                            }}
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: bg, border, borderRadius: 6, cursor, height: 36,
                            }}
                            title={dayTasks.length > 0 ? dayTasks.map(t => t.assignee + ': ' + t.status).join('\n') : 'No task'}
                          >
                            <span style={{ fontSize: 13, fontWeight: 800, color: textColor }}>{icon}</span>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* ── Modal popup for clicked day cell ── */}
            {ottExpanded.__ottModal && (() => {
              const md = ottExpanded.__ottModal;
              return (
                <div onClick={() => setOttExpanded({})} style={{
                  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                  background: "rgba(0,0,0,0.25)", zIndex: 9999,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <div onClick={e => e.stopPropagation()} style={{
                    width: 320, maxWidth: "90vw", maxHeight: "70vh", overflow: "auto",
                    padding: "16px 18px 16px 16px",
                    background: "linear-gradient(145deg, #f5f3ff 0%, #ede9fe 40%, #ddd6fe 100%)",
                    borderRadius: "6px 6px 6px 22px",
                    boxShadow: "3px 4px 20px rgba(0,0,0,0.18), inset 0 0 30px rgba(255,255,255,0.3)",
                    transform: "rotate(-0.5deg)", position: "relative",
                  }}>
                    <div style={{ position: "absolute", bottom: 0, right: 0, width: 28, height: 28, background: "linear-gradient(135deg, #ede9fe 50%, #b4a7d6 50%)", borderRadius: "12px 0 0 0" }} />
                    <div style={{ position: "absolute", top: -6, left: "50%", transform: "translateX(-50%) rotate(1deg)", width: 50, height: 14, background: "rgba(167,139,250,0.25)", borderRadius: 2 }} />
                    <button onClick={() => setOttExpanded({})} style={{ position: "absolute", top: 8, right: 12, background: "none", border: "none", fontSize: 15, cursor: "pointer", color: "#7c3aed", fontWeight: 700 }}>✕</button>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#5b21b6", marginBottom: 8 }}>
                      {md.date.toLocaleDateString("en-IN", { day: "numeric", month: "short", weekday: "short" })}
                      <span style={{ fontWeight: 500, fontSize: 11, color: "#7c3aed", marginLeft: 8 }}>{md.title}</span>
                    </div>
                    {md.tasks.map((task, idx) => {
                      const status = task.status || "new";
                      const isComplete = status.toLowerCase() === "completed";
                      const isDelayed = status.toLowerCase() === "delayed";
                      const statusColor = isComplete ? "#16a34a" : isDelayed ? "#dc2626" : "#d97706";
                      return (
                        <div key={idx} style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "5px 0", borderBottom: idx < md.tasks.length - 1 ? "1px dashed #c4b5fd" : "none",
                        }}>
                          {isSuper && !isComplete && task._id && (
                            <input type="checkbox" checked={ottSelected.has(task._id)} onChange={() => {
                              setOttSelected(prev => {
                                const next = new Set(prev);
                                if (next.has(task._id)) next.delete(task._id); else next.add(task._id);
                                return next;
                              });
                            }} style={{ width: 14, height: 14 }} />
                          )}
                          <span style={{ fontWeight: 600, fontSize: 12, color: "#374151", flex: 1 }}>{task.assignee || "—"}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: statusColor,
                            background: isComplete ? "rgba(22,163,74,0.12)" : isDelayed ? "rgba(220,38,38,0.10)" : "rgba(217,119,6,0.10)",
                            padding: "2px 8px", borderRadius: 6, textTransform: "capitalize",
                          }}>{status}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Legend */}
            <div style={{ display: "flex", gap: 14, marginTop: 12, fontSize: 11, color: "#64748b", flexWrap: "wrap" }}>
              <span><span style={{ color: "#16a34a", fontWeight: 700 }}>✓</span> Done</span>
              <span><span style={{ color: "#dc2626", fontWeight: 700 }}>✗</span> Delayed</span>
              <span><span style={{ color: "#d97706", fontWeight: 700 }}>!</span> Pending</span>
              <span><span style={{ color: "#93c5fd", fontWeight: 700 }}>—</span> No task</span>
              <span><span style={{ color: "#a78bfa", fontWeight: 700 }}>·</span> Upcoming</span>
            </div>
          </div>
          );
        })()}

        {filtered.length === 0 && <div className="rpt-empty">No task data available</div>}
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════
   *  Staff Planner Renderer
   * ═══════════════════════════════════════════════════════ */
  function renderPlanner() {
    // IST date helper — avoids UTC shift that causes wrong-day mapping
    function toIST(d) {
      const dt = new Date(d);
      const ist = new Date(dt.getTime() + 330 * 60000); // UTC+5:30
      return ist.toISOString().slice(0, 10);
    }

    const staffUsers = plannerStaff.map(s => s.username).sort();
    const dataUsers = Object.keys(plannerData).sort();
    const allUsers = [...new Set([...staffUsers, ...dataUsers])].sort();
    const filteredUsers = plannerFilterUser === "__all__" ? allUsers.filter(u => plannerData[u]) : [plannerFilterUser].filter(u => plannerData[u]);

    // Build date range — use plain string manipulation to avoid UTC shift
    const dates = [];
    const d1 = new Date(from + "T00:00:00+05:30");
    const d2 = new Date(to   + "T00:00:00+05:30");
    for (let d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
      dates.push(toIST(d));
    }

    function fmtTime(date) {
      if (!date) return "--:--";
      const dt = new Date(date);
      return dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
    }

    function fmtMins(mins) {
      if (!mins || mins <= 0) return "0m";
      const h = Math.floor(mins / 60);
      const m = Math.round(mins % 60);
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    }

    // Build timeline blocks for a user on a given date
    function buildTimeline(userData) {
      if (!userData) return { blocks: [], stats: {} };
      const att = userData.attendance;
      const appts = userData.appointments || [];

      // Sort appointments by start time
      const sessions = appts
        .filter(a => a.start && a.status !== "Cancelled" && a.type !== "Leave")
        .map(a => {
          const s = new Date(a.start);
          const e = a.end ? new Date(a.end) : new Date(s.getTime() + 60 * 60000);
          return {
            start: s, end: e,
            name: a.name || a.patientCard || a.title || "Session",
            status: a.status,
            isHelper: !!a._isHelper,
            leadTherapist: a.leadTherapist || "",
            helperTherapist1: a.helperTherapist1 || "",
            helperTherapist2: a.helperTherapist2 || "",
            type: a.type || "Therapy",
          };
        })
        .sort((a, b) => a.start - b.start);

      const checkIn = att?.checkInTime ? new Date(att.checkInTime) : null;
      const checkOut = att?.checkOutTime ? new Date(att.checkOutTime) : null;

      // Build blocks: checkin marker, sessions with gaps, checkout marker
      const blocks = [];
      let totalSessionMins = 0;
      let selfCount = 0;
      let helperCount = 0;
      let withSirCount = 0;
      const uniquePatients = new Set();

      if (checkIn) {
        blocks.push({ type: "checkin", time: checkIn, label: "Check In" });
      }

      let lastEnd = checkIn;
      sessions.forEach(s => {
        // Free time gap before this session
        if (lastEnd && s.start > lastEnd) {
          const gapMins = (s.start - lastEnd) / 60000;
          if (gapMins >= 5) {
            blocks.push({ type: "free", start: lastEnd, end: s.start, mins: gapMins });
          }
        }

        const durMins = (s.end - s.start) / 60000;
        totalSessionMins += durMins;

        // Determine role
        let role = "self";
        if (s.isHelper) {
          role = "helper";
          helperCount++;
        } else if (s.helperTherapist1 || s.helperTherapist2) {
          // This user is lead and has a helper — "with assistance"
          role = "assisted";
          selfCount++;
        } else {
          selfCount++;
        }

        // Check if "with sir" (doctor/owner in the session)
        const staffInfo = plannerStaff.find(st => st.username === s.leadTherapist);
        if (s.isHelper && staffInfo && (staffInfo.roles || []).some(r => ["owner", "doctor"].includes(r))) {
          withSirCount++;
        }

        if (s.name) uniquePatients.add(s.name);
        blocks.push({ type: "session", start: s.start, end: s.end, mins: durMins, name: s.name, status: s.status, role });

        if (s.end > (lastEnd || new Date(0))) lastEnd = s.end;
      });

      const DEFAULT_LUNCH_MINS = 40;

      // Lunch detection: find longest gap > 15 mins between 11 AM - 2 PM
      let lunchBlock = null;
      blocks.forEach(b => {
        if (b.type === "free" && b.mins >= 15) {
          const h = new Date(b.start).toLocaleString("en-IN", { hour: "numeric", hour12: false, timeZone: "Asia/Kolkata" });
          const hr = parseInt(h, 10);
          if (hr >= 11 && hr <= 14) {
            if (!lunchBlock || b.mins > lunchBlock.mins) {
              lunchBlock = b;
            }
          }
        }
      });
      if (lunchBlock) lunchBlock.type = "lunch";

      // Free time after last session before checkout
      if (lastEnd && checkOut && checkOut > lastEnd) {
        const gapMins = (checkOut - lastEnd) / 60000;
        if (gapMins >= 5) {
          blocks.push({ type: "free", start: lastEnd, end: checkOut, mins: gapMins });
        }
      }

      if (checkOut) {
        blocks.push({ type: "checkout", time: checkOut, label: "Check Out" });
      }

      const totalFreeMins = blocks.filter(b => b.type === "free").reduce((sum, b) => sum + b.mins, 0);
      // Default 40 min lunch if no gap detected in the 11-2 PM window
      const lunchMins = lunchBlock ? lunchBlock.mins : DEFAULT_LUNCH_MINS;
      // If lunch was detected from a free block, subtract it from free time; otherwise free stays as-is
      const actualFree = lunchBlock ? totalFreeMins - lunchMins : totalFreeMins;
      const workedMins = att?.workedMinutes || (checkIn && checkOut ? (checkOut - checkIn) / 60000 : 0);

      return {
        blocks,
        stats: {
          totalPatients: uniquePatients.size,
          selfCount,
          helperCount,
          withSirCount,
          lunchMins,
          freeMins: Math.max(0, actualFree),
          lunchDetected: !!lunchBlock,
          totalSessionMins,
          workedMins,
          checkIn, checkOut,
        },
      };
    }

    // Color mapping for block types
    const blockColors = {
      session: { bg: "#dbeafe", border: "#3b82f6", text: "#1e40af" },
      "session-helper": { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" },
      "session-assisted": { bg: "#e0e7ff", border: "#6366f1", text: "#3730a3" },
      free: { bg: "#f1f5f9", border: "#94a3b8", text: "#64748b" },
      lunch: { bg: "#fef2f2", border: "#f87171", text: "#991b1b" },
      checkin: { bg: "#dcfce7", border: "#16a34a", text: "#15803d" },
      checkout: { bg: "#fce7f3", border: "#ec4899", text: "#9d174d" },
    };

    return (
      <div>
        {/* Controls */}
        <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {isSuper && (
            <select value={plannerFilterUser} onChange={e => setPlannerFilterUser(e.target.value)}
              style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}>
              <option value="__all__">All Staff</option>
              {allUsers.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          )}
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {filteredUsers.length} staff &middot; {dates.length} day(s)
          </span>
        </div>

        {filteredUsers.length === 0 && (
          <div className="rpt-empty">No attendance data found for this date range</div>
        )}

        {/* Per-user cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 18 }}>
          {filteredUsers.map(u => {
            const userDates = dates.filter(d => plannerData[u]?.[d]);
            if (userDates.length === 0) return null;

            // Aggregate stats
            let aggPatients = 0, aggSelf = 0, aggHelper = 0, aggSir = 0, aggLunch = 0, aggFree = 0, aggWorked = 0;

            return (
              <div key={u} style={{
                background: "#fff", borderRadius: 18, overflow: "hidden",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
                border: "1px solid #e2e8f0",
              }}>
                {/* Header */}
                <div style={{
                  padding: "14px 18px",
                  background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  borderBottom: "1px solid #e2e8f0",
                }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>{u}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                    {plannerStaff.find(s => s.username === u)?.designation || "Staff"} &middot; {userDates.length} day(s)
                  </div>
                </div>

                {/* Per-date timeline */}
                <div style={{ padding: "10px 14px" }}>
                  {userDates.map(dateStr => {
                    const { blocks, stats } = buildTimeline(plannerData[u][dateStr]);
                    aggPatients += stats.totalPatients;
                    aggSelf += stats.selfCount;
                    aggHelper += stats.helperCount;
                    aggSir += stats.withSirCount;
                    aggLunch += stats.lunchMins;
                    aggFree += stats.freeMins;
                    aggWorked += stats.workedMins;

                    const dateLabel = new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

                    return (
                      <div key={dateStr} style={{
                        marginBottom: 14, padding: "10px 12px",
                        background: "#fafbfd", borderRadius: 12,
                        border: "1px solid #e2e8f0",
                      }}>
                        {/* Date header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{dateLabel}</div>
                          <div style={{ fontSize: 10, color: "#64748b" }}>
                            {stats.checkIn ? fmtTime(stats.checkIn) : "--"} → {stats.checkOut ? fmtTime(stats.checkOut) : "--"}
                          </div>
                        </div>

                        {/* Timeline blocks */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                          {blocks.map((b, i) => {
                            const colorKey = b.type === "session" ? (b.role === "helper" ? "session-helper" : b.role === "assisted" ? "session-assisted" : "session") : b.type;
                            const c = blockColors[colorKey] || blockColors.free;

                            if (b.type === "checkin" || b.type === "checkout") {
                              return (
                                <div key={i} style={{
                                  display: "flex", alignItems: "center", gap: 6,
                                  padding: "3px 8px", borderRadius: 6,
                                  background: c.bg, borderLeft: `3px solid ${c.border}`,
                                }}>
                                  <span style={{ fontSize: 10, fontWeight: 700, color: c.text }}>
                                    {b.type === "checkin" ? "🟢" : "🔴"} {fmtTime(b.time)}
                                  </span>
                                  <span style={{ fontSize: 10, color: c.text }}>{b.label}</span>
                                </div>
                              );
                            }

                            if (b.type === "free" || b.type === "lunch") {
                              return (
                                <div key={i} style={{
                                  display: "flex", alignItems: "center", gap: 6,
                                  padding: "2px 8px", borderRadius: 6,
                                  background: c.bg, borderLeft: `3px dashed ${c.border}`,
                                }}>
                                  <span style={{ fontSize: 10, color: c.text }}>
                                    {b.type === "lunch" ? "🍽️" : "⏸️"} {fmtTime(b.start)} — {fmtTime(b.end)}
                                  </span>
                                  <span style={{ fontSize: 10, fontWeight: 600, color: c.text }}>
                                    {b.type === "lunch" ? "Lunch" : "Free"} ({fmtMins(b.mins)})
                                  </span>
                                </div>
                              );
                            }

                            // Session block
                            return (
                              <div key={i} style={{
                                display: "flex", alignItems: "center", gap: 6,
                                padding: "4px 8px", borderRadius: 8,
                                background: c.bg, borderLeft: `3px solid ${c.border}`,
                              }}>
                                <span style={{ fontSize: 10, fontWeight: 600, color: c.text, whiteSpace: "nowrap" }}>
                                  {fmtTime(b.start)} — {fmtTime(b.end)}
                                </span>
                                <span style={{ fontSize: 11, fontWeight: 700, color: c.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {b.name}
                                </span>
                                <span style={{
                                  fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                                  background: b.role === "helper" ? "#fef3c7" : b.role === "assisted" ? "#e0e7ff" : "rgba(59,130,246,0.12)",
                                  color: b.role === "helper" ? "#92400e" : b.role === "assisted" ? "#3730a3" : "#1e40af",
                                }}>
                                  {b.role === "helper" ? "Helper" : b.role === "assisted" ? "Assisted" : "Lead"}
                                </span>
                                {b.status === "Completed" && <span style={{ fontSize: 10 }}>✅</span>}
                                {b.status === "Skipped" && <span style={{ fontSize: 10 }}>⏭️</span>}
                              </div>
                            );
                          })}

                          {blocks.length === 0 && (
                            <div style={{ fontSize: 11, color: "#94a3b8", padding: "4px 0" }}>No session data</div>
                          )}
                        </div>

                        {/* Day summary bar */}
                        <div style={{
                          display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8,
                          padding: "6px 8px", background: "#f1f5f9", borderRadius: 8,
                        }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#1e293b" }}>
                            👥 {stats.totalPatients} patients
                          </span>
                          <span style={{ fontSize: 10, color: "#3b82f6", fontWeight: 600 }}>
                            Self: {stats.selfCount}
                          </span>
                          {stats.helperCount > 0 && (
                            <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 600 }}>
                              Helper: {stats.helperCount}
                            </span>
                          )}
                          {stats.withSirCount > 0 && (
                            <span style={{ fontSize: 10, color: "#6366f1", fontWeight: 600 }}>
                              With Sir: {stats.withSirCount}
                            </span>
                          )}
                          <span style={{ fontSize: 10, color: "#f87171", fontWeight: 600 }}>
                            🍽️ {fmtMins(stats.lunchMins)}{!stats.lunchDetected ? " (default)" : ""}
                          </span>
                          <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
                            Free: {fmtMins(stats.freeMins > 0 ? stats.freeMins : 0)}
                          </span>
                          <span style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>
                            Total: {fmtMins(stats.workedMins)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Aggregate footer for multi-day range */}
                {userDates.length > 1 && (
                  <div style={{
                    padding: "10px 18px", background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                    borderTop: "1px solid #e2e8f0",
                    display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11,
                  }}>
                    <span style={{ fontWeight: 800, color: "#0f172a" }}>Totals:</span>
                    <span style={{ color: "#1e293b", fontWeight: 600 }}>👥 {aggPatients} patients</span>
                    <span style={{ color: "#3b82f6", fontWeight: 600 }}>Self: {aggSelf}</span>
                    {aggHelper > 0 && <span style={{ color: "#f59e0b", fontWeight: 600 }}>Helper: {aggHelper}</span>}
                    {aggSir > 0 && <span style={{ color: "#6366f1", fontWeight: 600 }}>With Sir: {aggSir}</span>}
                    <span style={{ color: "#f87171", fontWeight: 600 }}>🍽️ {fmtMins(aggLunch)}</span>
                    <span style={{ color: "#94a3b8", fontWeight: 600 }}>Free: {fmtMins(aggFree > 0 ? aggFree : 0)}</span>
                    <span style={{ color: "#64748b", fontWeight: 600 }}>Worked: {fmtMins(aggWorked)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 14, marginTop: 16, fontSize: 11, color: "#64748b", flexWrap: "wrap" }}>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#dbeafe", border: "1px solid #3b82f6", borderRadius: 2, marginRight: 4 }}></span>Lead</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 2, marginRight: 4 }}></span>Helper</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#e0e7ff", border: "1px solid #6366f1", borderRadius: 2, marginRight: 4 }}></span>Assisted</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#f1f5f9", border: "1px solid #94a3b8", borderRadius: 2, marginRight: 4 }}></span>Free</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#fef2f2", border: "1px solid #f87171", borderRadius: 2, marginRight: 4 }}></span>Lunch</span>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════
   *  Attendance Tab (Patient Sessions + Staff) — Enhanced 5D
   * ═══════════════════════════════════════════════════════ */
  function renderAttendance() {
    var toIST = function(d) { return new Date(new Date(d).getTime() + 330 * 60000).toISOString().slice(0, 10); };
    var fmtTime = function(d) {
      if (!d) return "--";
      return new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
    };

    // ── Patient sessions: group by date, then sort by time ──
    var completed = attPatientSessions.filter(function(a) { return a.status === "Completed"; });
    var skipped   = attPatientSessions.filter(function(a) { return a.status === "Skipped"; });
    var pending   = attPatientSessions.filter(function(a) { return ["scheduled", "Pending", "inprogress"].includes(a.status); });
    var uniquePatients = new Set(completed.map(function(a) { return a.patientCard; }).filter(Boolean));

    // Per-therapist session counts (ALL therapists, no truncation)
    var therapistCounts = {};
    completed.forEach(function(a) { if (a.username) therapistCounts[a.username] = (therapistCounts[a.username] || 0) + 1; });
    var allTherapistsSorted = Object.entries(therapistCounts).sort(function(a, b) { return b[1] - a[1]; });

    // Group patient sessions by date (IST)
    var patByDate = {};
    attPatientSessions.forEach(function(a) {
      var d = a.date ? toIST(a.date) : "unknown";
      if (!patByDate[d]) patByDate[d] = [];
      patByDate[d].push(a);
    });
    var patDates = Object.keys(patByDate).sort();

    // ── Staff attendance ──
    var staffCompleted = attStaffRecords.filter(function(r) { return r.status === "COMPLETED"; });
    var staffInProg    = attStaffRecords.filter(function(r) { return r.status === "IN_PROGRESS"; });
    var avgWorked      = staffCompleted.length > 0 ? Math.round(staffCompleted.reduce(function(s, r) { return s + (r.workedMinutes || 0); }, 0) / staffCompleted.length) : 0;
    var totalWorkedAll = attStaffRecords.reduce(function(s, r) { return s + (r.workedMinutes || 0); }, 0);

    // Staff by date + detect absentees
    var staffByDate = {};
    attStaffRecords.forEach(function(r) {
      var d = r.date || "unknown";
      if (!staffByDate[d]) staffByDate[d] = [];
      staffByDate[d].push(r);
    });

    // Build set of all staff usernames for absentee detection
    var allStaffNames = attAllStaff.map(function(u) { return (u.username || "").toLowerCase(); }).filter(Boolean);
    var staffNameMap = {};
    attAllStaff.forEach(function(u) {
      staffNameMap[(u.username || "").toLowerCase()] = { designation: u.designation || "", name: u.name || u.username || "" };
    });

    // ── Merge all dates from both patient + staff for parallel layout ──
    var allDatesSet = {};
    patDates.forEach(function(d) { allDatesSet[d] = true; });
    Object.keys(staffByDate).forEach(function(d) { allDatesSet[d] = true; });
    var allDates = Object.keys(allDatesSet).sort();

    return (
      <div>
        {/* ══════ Summary row — combined KPIs ══════ */}
        <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          {/* Patient KPIs */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, color: "#7b6c00", marginBottom: 8 }}>Saadhak Sessions</h3>
            <div className="rpt-summary-row" style={{ marginBottom: 0 }}>
              <Kpi label="Total" value={fmtNum(attPatientSessions.length)} />
              <Kpi label="Completed" value={fmtNum(completed.length)} color="#388e3c" />
              <Kpi label="Skipped" value={fmtNum(skipped.length)} color="#e53935" />
              <Kpi label="Pending" value={fmtNum(pending.length)} color="#f57c00" />
              <Kpi label="Unique Patients" value={fmtNum(uniquePatients.size)} color="#1976d2" />
            </div>
          </div>
          {/* Staff KPIs */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, color: "#5b21b6", marginBottom: 8 }}>Staff Attendance</h3>
            <div className="rpt-summary-row" style={{ marginBottom: 0 }}>
              <Kpi label="Total" value={fmtNum(attStaffRecords.length)} />
              <Kpi label="Shift Done" value={fmtNum(staffCompleted.length)} color="#388e3c" />
              <Kpi label="Working" value={fmtNum(staffInProg.length)} color="#f57c00" />
              <Kpi label="Avg Worked" value={Math.floor(avgWorked / 60) + "h " + (avgWorked % 60) + "m"} color="#1976d2" />
              <Kpi label="Total Hours" value={Math.floor(totalWorkedAll / 60) + "h"} color="#6366f1" />
            </div>
          </div>
        </div>

        {/* All therapists strip */}
        {allTherapistsSorted.length > 0 && (
          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap", padding: "8px 12px", background: "#f8fafc", borderRadius: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>Therapists (completed):</span>
            {allTherapistsSorted.map(function(entry) {
              return (
                <span key={entry[0]} style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", background: "#e0e7ff", padding: "2px 10px", borderRadius: 8 }}>
                  {entry[0]}: {entry[1]}
                </span>
              );
            })}
          </div>
        )}

        {/* ══════ Missed Sessions Card ══════ */}
        {(function() {
          var now = new Date();
          var missedAppts = attPatientSessions.filter(function(a) {
            if (a.status === "Skipped") return true;
            if ((a.status === "scheduled" || a.status === "Pending") && new Date(a.date) < now) return true;
            return false;
          });
          if (missedAppts.length === 0) return null;

          // Group by patient
          var byPatient = {};
          missedAppts.forEach(function(a) {
            var key = a.patientCard || a.name || "Unknown";
            if (!byPatient[key]) byPatient[key] = { name: a.name || key, patientCard: key, sessions: [] };
            byPatient[key].sessions.push(a);
          });
          var patientList = Object.values(byPatient);

          // Count with/without comments
          var withComments = 0;
          var withoutComments = 0;
          missedAppts.forEach(function(a) {
            if (a.sessionComments && a.sessionComments.trim()) withComments++;
            else withoutComments++;
          });

          // Filter
          var filtered = patientList;
          if (missedFilter === 'with') {
            filtered = patientList.filter(function(p) { return p.sessions.some(function(s) { return s.sessionComments && s.sessionComments.trim(); }); });
          } else if (missedFilter === 'without') {
            filtered = patientList.filter(function(p) { return p.sessions.some(function(s) { return !s.sessionComments || !s.sessionComments.trim(); }); });
          }

          return (
            <div style={{
              marginBottom: 18, background: "linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)",
              borderRadius: 16, border: "1.5px solid #fecaca", overflow: "hidden",
              boxShadow: "0 4px 16px rgba(239,68,68,0.08)",
            }}>
              {/* Header */}
              <div style={{ padding: "14px 16px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#991b1b" }}>
                    Missed Sessions ({missedAppts.length})
                  </h3>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                    {patientList.length} patients missed their sessions
                  </div>
                </div>
                {/* Count filter pills */}
                <div style={{ display: "flex", gap: 6 }}>
                  {[
                    { key: "all", label: "All", count: missedAppts.length, bg: "#fee2e2", color: "#991b1b" },
                    { key: "with", label: "With Comments", count: withComments, bg: "#dcfce7", color: "#166534" },
                    { key: "without", label: "No Comments", count: withoutComments, bg: "#fef3c7", color: "#92400e" },
                  ].map(function(f) {
                    var isActive = missedFilter === f.key;
                    return (
                      <button key={f.key} onClick={function() { setMissedFilter(f.key); }}
                        style={{
                          padding: "4px 10px", borderRadius: 8, border: "1.5px solid " + (isActive ? f.color : "#e5e7eb"),
                          background: isActive ? f.bg : "#fff", color: isActive ? f.color : "#6b7280",
                          fontSize: 11, fontWeight: 700, cursor: "pointer",
                        }}>
                        {f.label} ({f.count})
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Patient rows */}
              <div style={{ padding: "0 12px 12px" }}>
                {filtered.map(function(p) {
                  var isExpanded = missedExpanded[p.patientCard];
                  var hasComment = p.sessions.some(function(s) { return s.sessionComments && s.sessionComments.trim(); });
                  var allHaveComments = p.sessions.every(function(s) { return s.sessionComments && s.sessionComments.trim(); });
                  var borderColor = allHaveComments ? "#86efac" : hasComment ? "#fde68a" : "#fca5a5";
                  var bgColor = allHaveComments ? "#f0fdf4" : hasComment ? "#fffbeb" : "#fef2f2";

                  return (
                    <div key={p.patientCard} style={{ marginBottom: 6 }}>
                      {/* Patient row — clickable */}
                      <div
                        onClick={function() { setMissedExpanded(function(prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[p.patientCard] = !prev[p.patientCard]; return n; }); }}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "8px 12px", borderRadius: isExpanded ? "10px 10px 0 0" : 10,
                          background: bgColor, border: "1.5px solid " + borderColor,
                          cursor: "pointer", transition: "all 0.15s",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{
                            width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                            background: allHaveComments ? "#22c55e" : hasComment ? "#f59e0b" : "#ef4444",
                          }} />
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{p.name}</span>
                          <span style={{ fontSize: 10, color: "#94a3b8" }}>{p.patientCard}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", background: "#fee2e2", padding: "2px 8px", borderRadius: 6 }}>
                            {p.sessions.length} missed
                          </span>
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>{isExpanded ? "▾" : "▸"}</span>
                        </div>
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div style={{
                          border: "1.5px solid " + borderColor, borderTop: "none",
                          borderRadius: "0 0 10px 10px", padding: "8px 12px",
                          background: "#fff",
                        }}>
                          {p.sessions.map(function(s, idx) {
                            var hasC = s.sessionComments && s.sessionComments.trim();
                            var dateStr = s.date ? new Date(s.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", weekday: "short" }) : "—";
                            var timeStr = s.start ? fmtTime(s.start) : "";
                            return (
                              <div key={s._id || idx} style={{
                                display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                                padding: "6px 0", borderBottom: idx < p.sessions.length - 1 ? "1px solid #f1f5f9" : "none",
                              }}>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
                                    {dateStr} {timeStr && <span style={{ color: "#94a3b8", fontWeight: 400 }}>{timeStr}</span>}
                                  </div>
                                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                    Therapist: <strong>{s.username || "—"}</strong>
                                    {s.status === "Skipped" && <span style={{ color: "#dc2626", marginLeft: 6 }}>(Skipped)</span>}
                                    {s.status !== "Skipped" && <span style={{ color: "#f59e0b", marginLeft: 6 }}>(No-show)</span>}
                                  </div>
                                  {hasC && (
                                    <div style={{ fontSize: 11, color: "#16a34a", marginTop: 3, padding: "3px 8px", background: "#f0fdf4", borderRadius: 6, display: "inline-block" }}>
                                      {s.sessionComments}
                                    </div>
                                  )}
                                  {!hasC && (
                                    <div style={{ fontSize: 10, color: "#dc2626", marginTop: 3, fontStyle: "italic" }}>
                                      No follow-up comment recorded
                                    </div>
                                  )}
                                </div>
                                <span style={{
                                  width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginTop: 4,
                                  background: hasC ? "#22c55e" : "#ef4444",
                                }} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {filtered.length === 0 && (
                  <div style={{ textAlign: "center", padding: 16, color: "#94a3b8", fontSize: 12 }}>
                    No patients match this filter.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ══════ Per-date parallel cards: Patient (left) + Staff (right) ══════ */}
        {allDates.map(function(d) {
          var dateLabel = new Date(d + "T00:00:00+05:30").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

          // ── Patient data for this date ──
          var sessions = (patByDate[d] || []).slice();
          sessions.sort(function(a, b) { return new Date(a.start || a.date || 0) - new Date(b.start || b.date || 0); });
          var dayCompleted = sessions.filter(function(a) { return a.status === "Completed"; }).length;
          var daySkipped   = sessions.filter(function(a) { return a.status === "Skipped"; }).length;
          var dayPending   = sessions.filter(function(a) { return ["scheduled", "Pending", "inprogress"].includes(a.status); }).length;
          var dayPatients  = new Set(sessions.map(function(a) { return a.patientCard; }).filter(Boolean));
          var dayTherapists = new Set(sessions.map(function(a) { return a.username; }).filter(Boolean));
          var pct = sessions.length > 0 ? Math.round(dayCompleted / sessions.length * 100) : 0;

          // ── Staff data for this date ──
          var dayStaff = (staffByDate[d] || []).slice();
          dayStaff.sort(function(a, b) { return new Date(a.checkInTime || 0) - new Date(b.checkInTime || 0); });
          var dayCompleted2 = dayStaff.filter(function(r) { return r.status === "COMPLETED"; }).length;
          var dayInProg2    = dayStaff.filter(function(r) { return r.status === "IN_PROGRESS"; }).length;
          var dayTotalMins  = dayStaff.reduce(function(s, r) { return s + (r.workedMinutes || 0); }, 0);
          var avgMins2 = dayStaff.length > 0 ? Math.round(dayTotalMins / dayStaff.length) : 0;

          // Absentees
          var presentNames = {};
          dayStaff.forEach(function(r) { presentNames[(r.username || "").toLowerCase()] = true; });
          var absentees = allStaffNames.filter(function(name) { return !presentNames[name]; });

          return (
            <div key={d} style={{ marginBottom: 22 }}>
              {/* Date heading */}
              <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a", marginBottom: 8, padding: "4px 8px", background: "#f1f5f9", borderRadius: 8, display: "inline-block" }}>
                {dateLabel}
              </div>

              {/* Two-column parallel layout */}
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>

                {/* ── LEFT: Patient card (yellow) ── */}
                <div style={{
                  flex: 1, minWidth: 280,
                  background: "linear-gradient(145deg, #fffde7 0%, #fff9c4 40%, #fff59d 100%)",
                  borderRadius: 18, overflow: "hidden", position: "relative",
                  boxShadow: "0 6px 20px rgba(245,197,24,0.10), 0 2px 6px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.8)",
                  border: "1px solid #f0e68c",
                }}>
                  {/* Header */}
                  <div style={{ padding: "12px 14px 8px", borderBottom: "1px dashed #e6d86e" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 800, fontSize: 13, color: "#7b6c00" }}>Saadhak Sessions</span>
                      <span style={{ fontSize: 18, fontWeight: 900, color: pct >= 80 ? "#16a34a" : pct >= 50 ? "#b8860b" : "#dc2626" }}>{pct}%</span>
                    </div>
                    <div style={{ marginTop: 4, background: "rgba(245,197,24,0.25)", borderRadius: 4, height: 5, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 4, width: pct + "%", background: pct >= 80 ? "#16a34a" : pct >= 50 ? "#b8860b" : "#ef4444" }} />
                    </div>
                    <div style={{ display: "flex", gap: 10, fontSize: 11, marginTop: 6, flexWrap: "wrap" }}>
                      <span style={{ color: "#16a34a", fontWeight: 800 }}>Completed: {dayCompleted}</span>
                      <span style={{ color: "#dc2626", fontWeight: 800 }}>Skipped: {daySkipped}</span>
                      <span style={{ color: "#d97706", fontWeight: 800 }}>Pending: {dayPending}</span>
                      <span style={{ color: "#5b21b6", fontWeight: 700 }}>{dayPatients.size} patients</span>
                      <span style={{ color: "#5b21b6", fontWeight: 700 }}>{dayTherapists.size} therapists</span>
                    </div>
                  </div>
                  {/* Patient list */}
                  <div style={{ padding: "6px 10px 12px" }}>
                    {sessions.length === 0 && <div style={{ fontSize: 11, color: "#94a3b8", padding: 8 }}>No sessions</div>}
                    {sessions.map(function(a, idx) {
                      var time = fmtTime(a.start || a.date);
                      var endTime = a.end ? fmtTime(a.end) : "";
                      var isCompleted = a.status === "Completed";
                      var isSkipped = a.status === "Skipped";
                      var statusColor = isCompleted ? "#16a34a" : isSkipped ? "#dc2626" : "#d97706";
                      var statusIcon  = isCompleted ? "✓" : isSkipped ? "✗" : "⏳";
                      var statusLabel = isCompleted ? "Completed" : isSkipped ? "Skipped" : (a.status || "Scheduled");
                      return (
                        <div key={a._id || idx} style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "5px 8px", marginBottom: 3, borderRadius: 10,
                          background: isCompleted ? "rgba(22,163,74,0.06)" : isSkipped ? "rgba(220,38,38,0.06)" : "rgba(217,119,6,0.06)",
                          borderLeft: "3px solid " + statusColor,
                        }}>
                          <div style={{ minWidth: 60, flexShrink: 0 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#5b21b6" }}>{time}</div>
                            {endTime && <div style={{ fontSize: 8, color: "#94a3b8" }}>{endTime}</div>}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {a.name || a.patientCard || "—"}
                            </div>
                            <div style={{ fontSize: 9, color: "#64748b" }}>
                              {a.patientCard || ""}{a.username ? " | " + a.username : ""}
                            </div>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <span style={{ fontSize: 10, fontWeight: 800, color: statusColor }}>{statusIcon} {statusLabel}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Folded corner */}
                  <div style={{ position: "absolute", bottom: 0, right: 0, width: 24, height: 24, background: "linear-gradient(135deg, #fff9c4 50%, #e6d86e 50%)", borderTopLeftRadius: 6, zIndex: 2 }} />
                </div>

                {/* ── RIGHT: Staff card (purple) ── */}
                <div style={{
                  flex: 1, minWidth: 280,
                  background: "linear-gradient(145deg, #f5f3ff 0%, #ede9fe 40%, #ddd6fe 100%)",
                  borderRadius: 18, overflow: "hidden", position: "relative",
                  boxShadow: "0 6px 20px rgba(124,58,237,0.10), 0 2px 6px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)",
                  border: "1px solid #c4b5fd",
                }}>
                  {/* Header */}
                  <div style={{ padding: "12px 14px 8px", borderBottom: "1px dashed #c4b5fd" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 800, fontSize: 13, color: "#5b21b6" }}>Staff Attendance</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>
                        Avg: {Math.floor(avgMins2 / 60)}h {avgMins2 % 60}m
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 10, fontSize: 11, marginTop: 6, flexWrap: "wrap" }}>
                      <span style={{ color: "#1e293b", fontWeight: 800 }}>Present: {dayStaff.length}</span>
                      <span style={{ color: "#16a34a", fontWeight: 700 }}>Shift Done: {dayCompleted2}</span>
                      {dayInProg2 > 0 && <span style={{ color: "#d97706", fontWeight: 700 }}>Still Working: {dayInProg2}</span>}
                      {absentees.length > 0 && <span style={{ color: "#dc2626", fontWeight: 700 }}>Absent: {absentees.length}</span>}
                    </div>
                  </div>
                  {/* Staff list */}
                  <div style={{ padding: "6px 10px 6px" }}>
                    {dayStaff.length === 0 && <div style={{ fontSize: 11, color: "#94a3b8", padding: 8 }}>No records</div>}
                    {dayStaff.map(function(r, idx) {
                      var hrs = Math.floor((r.workedMinutes || 0) / 60);
                      var mins = (r.workedMinutes || 0) % 60;
                      var checkIn = fmtTime(r.checkInTime);
                      var checkOut = fmtTime(r.checkOutTime);
                      var isComp = r.status === "COMPLETED";
                      return (
                        <div key={r._id || idx} style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "4px 8px", marginBottom: 3, borderRadius: 10,
                          background: isComp ? "rgba(22,163,74,0.06)" : "rgba(217,119,6,0.06)",
                          borderLeft: "3px solid " + (isComp ? "#16a34a" : "#d97706"),
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#1e293b" }}>{r.username}</span>
                            {r.designation && <span style={{ fontSize: 8, color: "#64748b", marginLeft: 3 }}>({r.designation})</span>}
                          </div>
                          <div style={{ fontSize: 9, color: "#6366f1", fontWeight: 600, flexShrink: 0 }}>
                            {checkIn} → {checkOut}
                          </div>
                          <div style={{ fontSize: 10, fontWeight: 800, color: "#1e293b", minWidth: 44, textAlign: "right", flexShrink: 0 }}>
                            {hrs}h {mins}m
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Absentees */}
                  {absentees.length > 0 && (
                    <div style={{ padding: "4px 10px 12px", borderTop: "1px dashed #fca5a5" }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: "#dc2626", marginBottom: 3, marginTop: 3 }}>Absent ({absentees.length}):</div>
                      <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                        {absentees.map(function(name) {
                          var info = staffNameMap[name] || {};
                          return (
                            <span key={name} style={{
                              fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 8,
                              background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.15)",
                            }}>
                              {info.name || name}
                              {info.designation ? " (" + info.designation + ")" : ""}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Folded corner */}
                  <div style={{ position: "absolute", bottom: 0, right: 0, width: 24, height: 24, background: "linear-gradient(135deg, #ede9fe 50%, #b4a7d6 50%)", borderTopLeftRadius: 6, zIndex: 2 }} />
                </div>

              </div>{/* end 2-col flex */}
            </div>
          );
        })}

        {attPatientSessions.length === 0 && attStaffRecords.length === 0 && (
          <div className="rpt-empty">No attendance data for this date range</div>
        )}
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════
   *  Revenue Tab (Billing, ReRoot, Discounts, Charity) — Enhanced 5D
   * ═══════════════════════════════════════════════════════ */
  function renderRevenue() {
    // 5D sticky card helpers
    const stickyNote = (bg, accent) => ({
      background: bg, borderRadius: 18, padding: "16px 18px", position: "relative",
      boxShadow: "0 8px 24px " + accent + "18, 0 2px 6px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.6)",
      border: "1px solid " + accent + "44",
      transform: "perspective(800px) rotateX(1deg)", transition: "all 0.25s ease",
    });
    const foldCorner = (accent) => ({
      position: "absolute", bottom: 0, right: 0, width: 24, height: 24,
      background: "linear-gradient(135deg, " + accent + "22 50%, " + accent + "88 50%)",
      borderTopLeftRadius: 8, zIndex: 2,
    });

    // ── Billing aggregations ──
    let totalBilled = 0, totalPaid = 0, totalDiscount = 0, totalBalance = 0;
    let rerootTotal = 0, rerootQty = 0;
    const rerootItems = {};
    const billsByOrg = {}; // billorg → { total, paid, count }
    const payModes = {}; // paymentmode → count

    revBillings.forEach(bill => {
      totalBilled   += (bill.total || 0);
      totalPaid     += (bill.paid || 0);
      totalDiscount += (bill.discount || 0);
      totalBalance  += (bill.balance || 0);

      const org = bill.billorg || "Other";
      if (!billsByOrg[org]) billsByOrg[org] = { total: 0, paid: 0, count: 0 };
      billsByOrg[org].total += (bill.total || 0);
      billsByOrg[org].paid += (bill.paid || 0);
      billsByOrg[org].count++;

      const pm = bill.paymentmode || "Unknown";
      payModes[pm] = (payModes[pm] || 0) + 1;

      (bill.services || []).forEach(svc => {
        if (svc.org === "ReRoot") {
          const qty = svc.qty || 1;
          const amt = svc.amount || 0;
          rerootTotal += amt;
          rerootQty += qty;
          const name = svc.name || "Unknown Item";
          if (!rerootItems[name]) rerootItems[name] = { qty: 0, amount: 0 };
          rerootItems[name].qty += qty;
          rerootItems[name].amount += amt;
        }
      });
    });

    // Package/session line items
    let packagesSoldValue = 0, packagesSoldCount = 0;
    const packageDetails = [];
    revBillings.forEach(bill => {
      (bill.services || []).forEach(svc => {
        const name = (svc.name || "").toLowerCase();
        if (name.includes("package") || name.includes("session") || name.includes("therapy") || name.includes("consultation")) {
          packagesSoldValue += (svc.amount || 0);
          packagesSoldCount++;
          packageDetails.push({ name: svc.name, amount: svc.amount || 0, qty: svc.qty || 1, patient: bill.name || bill.patientCard, date: bill.invoicedate, org: bill.billorg });
        }
      });
    });

    // ── Charity / Free sessions ──
    const completedTherapy = revAppts.filter(a => a.status === "Completed");
    const charityCount = completedTherapy.filter(a => (a.visitCharges === 0 || a.visitCharges === undefined || a.visitCharges === null) && a.paymentStatus === "not_applicable").length;
    const paidSessionCount = completedTherapy.filter(a => a.visitCharges > 0).length;
    const totalVisitCharges = completedTherapy.reduce((s, a) => s + (a.visitCharges || 0), 0);
    // Avg per session
    const avgPerSession = paidSessionCount > 0 ? Math.round(totalVisitCharges / paidSessionCount) : 0;
    const collectionRate = totalBilled > 0 ? Math.round((totalPaid / totalBilled) * 100) : 0;

    const rerootSorted = Object.entries(rerootItems).sort((a, b) => b[1].amount - a[1].amount);

    return (
      <div>
        {/* ── Top KPIs ── */}
        <div className="rpt-summary-row" style={{ marginBottom: 16 }}>
          <Kpi label="Total Billed" value={"₹" + fmtNum(totalBilled)} color="#1976d2" />
          <Kpi label="Total Paid" value={"₹" + fmtNum(totalPaid)} color="#388e3c" />
          <Kpi label="Discounts" value={"₹" + fmtNum(totalDiscount)} color="#f57c00" />
          <Kpi label="Balance Due" value={"₹" + fmtNum(totalBalance)} color="#e53935" />
          <Kpi label="Bills" value={fmtNum(revBillings.length)} />
          <Kpi label="Collection %" value={collectionRate + "%"} color={collectionRate >= 80 ? "#16a34a" : "#dc2626"} />
        </div>

        {/* ── Revenue 5D Sticky Cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, marginBottom: 28 }}>
          {/* Session Charges */}
          <div style={stickyNote("linear-gradient(145deg, #dcfce7 0%, #bbf7d0 100%)", "#16a34a")}>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#16a34a" }}>₹{fmtNum(totalVisitCharges)}</div>
            <div style={{ fontSize: 12, color: "#15803d", fontWeight: 700, marginTop: 2 }}>Session Charges</div>
            <div style={{ fontSize: 10, color: "#166534", marginTop: 6, lineHeight: 1.5 }}>
              {paidSessionCount} paid sessions<br />
              Avg ₹{fmtNum(avgPerSession)} per session<br />
              {completedTherapy.length} total completed
            </div>
            <div style={foldCorner("#16a34a")} />
          </div>

          {/* Packages Sold */}
          <div style={stickyNote("linear-gradient(145deg, #fef3c7 0%, #fde68a 100%)", "#d97706")}>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#d97706" }}>₹{fmtNum(packagesSoldValue)}</div>
            <div style={{ fontSize: 12, color: "#92400e", fontWeight: 700, marginTop: 2 }}>Packages Sold</div>
            <div style={{ fontSize: 10, color: "#78350f", marginTop: 6, lineHeight: 1.5 }}>
              {packagesSoldCount} line items<br />
              Avg ₹{fmtNum(packagesSoldCount > 0 ? Math.round(packagesSoldValue / packagesSoldCount) : 0)} per package
            </div>
            <div style={foldCorner("#d97706")} />
          </div>

          {/* Free/Charity */}
          <div style={stickyNote("linear-gradient(145deg, #e0e7ff 0%, #c7d2fe 100%)", "#6366f1")}>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#6366f1" }}>{charityCount}</div>
            <div style={{ fontSize: 12, color: "#3730a3", fontWeight: 700, marginTop: 2 }}>Free / Charity</div>
            <div style={{ fontSize: 10, color: "#312e81", marginTop: 6, lineHeight: 1.5 }}>
              Sessions with no charge<br />
              {completedTherapy.length > 0 ? Math.round(charityCount / completedTherapy.length * 100) : 0}% of all sessions
            </div>
            <div style={foldCorner("#6366f1")} />
          </div>

          {/* Discounts */}
          <div style={stickyNote("linear-gradient(145deg, #fef2f2 0%, #fecaca 100%)", "#dc2626")}>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#dc2626" }}>₹{fmtNum(totalDiscount)}</div>
            <div style={{ fontSize: 12, color: "#991b1b", fontWeight: 700, marginTop: 2 }}>Total Discounts</div>
            <div style={{ fontSize: 10, color: "#7f1d1d", marginTop: 6, lineHeight: 1.5 }}>
              {revBillings.filter(b => (b.discount || 0) > 0).length} discounted bills<br />
              Avg ₹{fmtNum(revBillings.filter(b => (b.discount || 0) > 0).length > 0 ? Math.round(totalDiscount / revBillings.filter(b => (b.discount || 0) > 0).length) : 0)} per discount
            </div>
            <div style={foldCorner("#dc2626")} />
          </div>

          {/* Balance Due */}
          {totalBalance > 0 && (
            <div style={stickyNote("linear-gradient(145deg, #fff7ed 0%, #fed7aa 100%)", "#ea580c")}>
              <div style={{ fontSize: 24, fontWeight: 900, color: "#ea580c" }}>₹{fmtNum(totalBalance)}</div>
              <div style={{ fontSize: 12, color: "#9a3412", fontWeight: 700, marginTop: 2 }}>Outstanding Balance</div>
              <div style={{ fontSize: 10, color: "#7c2d12", marginTop: 6, lineHeight: 1.5 }}>
                {revBillings.filter(b => (b.balance || 0) > 0).length} unpaid bills<br />
                Collection rate: {collectionRate}%
              </div>
              <div style={foldCorner("#ea580c")} />
            </div>
          )}
        </div>

        {/* ── Org-wise & Payment Mode Breakdown ── */}
        {Object.keys(billsByOrg).length > 0 && (
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240, background: "#f8fafc", borderRadius: 14, padding: "12px 16px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: "#0f172a", marginBottom: 8 }}>By Organisation</div>
              {Object.entries(billsByOrg).sort((a,b) => b[1].total - a[1].total).map(([org, d]) => (
                <div key={org} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #f1f5f9", fontSize: 12 }}>
                  <span style={{ fontWeight: 600, color: "#374151" }}>{org} ({d.count})</span>
                  <span style={{ fontWeight: 800, color: "#16a34a" }}>₹{fmtNum(d.paid)} / ₹{fmtNum(d.total)}</span>
                </div>
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 200, background: "#f8fafc", borderRadius: 14, padding: "12px 16px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: "#0f172a", marginBottom: 8 }}>Payment Modes</div>
              {Object.entries(payModes).sort((a,b) => b[1] - a[1]).map(([mode, cnt]) => (
                <div key={mode} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #f1f5f9", fontSize: 12 }}>
                  <span style={{ fontWeight: 600, color: "#374151", textTransform: "capitalize" }}>{mode}</span>
                  <span style={{ fontWeight: 800, color: "#6366f1" }}>{cnt} bills</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ReRoot Items Sold ── */}
        {rerootSorted.length > 0 && (
          <>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>🛒 ReRoot Items Sold</h3>
            <div className="rpt-summary-row" style={{ marginBottom: 12 }}>
              <Kpi label="ReRoot Revenue" value={"₹" + fmtNum(rerootTotal)} color="#16a34a" />
              <Kpi label="Items Sold" value={fmtNum(rerootQty)} color="#1976d2" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10, marginBottom: 24 }}>
              {rerootSorted.map(function(entry) {
                var name = entry[0], data = entry[1];
                return (
                <div key={name} style={{
                  background: "#fff", borderRadius: 12, padding: "10px 14px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0",
                  borderLeft: "4px solid #16a34a",
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 4 }}>{name}</div>
                  <div style={{ display: "flex", gap: 12, fontSize: 11 }}>
                    <span style={{ color: "#64748b" }}>Qty: <strong style={{ color: "#1e293b" }}>{data.qty}</strong></span>
                    <span style={{ color: "#64748b" }}>Amt: <strong style={{ color: "#16a34a" }}>₹{fmtNum(data.amount)}</strong></span>
                    <span style={{ color: "#64748b" }}>Avg: <strong style={{ color: "#6366f1" }}>₹{fmtNum(data.qty > 0 ? Math.round(data.amount / data.qty) : 0)}</strong></span>
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── Discount Details ── */}
        {totalDiscount > 0 && (
          <>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>🏷️ Discount Breakdown</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginBottom: 16 }}>
              {revBillings.filter(function(b) { return (b.discount || 0) > 0; }).sort(function(a, b) { return (b.discount || 0) - (a.discount || 0); }).slice(0, 30).map(function(b) {
                var discPct = b.total > 0 ? Math.round((b.discount / b.total) * 100) : 0;
                return (
                <div key={b._id} style={{
                  background: "#fff", borderRadius: 10, padding: "8px 12px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)", border: "1px solid #e2e8f0",
                  borderLeft: "4px solid #f59e0b",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 12, color: "#1e293b" }}>{b.name || b.patientCard}</div>
                      <div style={{ fontSize: 10, color: "#64748b" }}>
                        {fmt(b.invoicedate)} &middot; {b.billorg || ""} &middot; Bill: ₹{fmtNum(b.total)} &middot; Paid: ₹{fmtNum(b.paid)}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontWeight: 800, fontSize: 14, color: "#dc2626" }}>-₹{fmtNum(b.discount)}</div>
                      <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600 }}>{discPct}% off</div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}

        {revBillings.length === 0 && revAppts.length === 0 && (
          <div className="rpt-empty">No billing or session data for this date range</div>
        )}
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════
   *  Enrollments Tab
   * ═══════════════════════════════════════════════════════ */
  function renderEnrollments() {
    // Group by date
    const byDate = {};
    enrollPatients.forEach(p => {
      const d = p.registrationDate ? new Date(p.registrationDate).toISOString().slice(0, 10) : "unknown";
      if (!byDate[d]) byDate[d] = [];
      byDate[d].push(p);
    });
    const sortedDates = Object.keys(byDate).sort().reverse();

    // Aggregate session sets for package details
    let totalPackageValue = 0;
    let totalSessionsBought = 0;
    enrollPatients.forEach(p => {
      (p.sessionSets || []).forEach(ss => {
        totalSessionsBought += (ss.qty || 0);
        totalPackageValue += ((ss.rate || 0) * (ss.qty || 0));
      });
    });

    return (
      <div>
        <div className="rpt-summary-row" style={{ marginBottom: 16 }}>
          <Kpi label="New Enrollments" value={fmtNum(enrollPatients.length)} color="#1976d2" />
          <Kpi label="Sessions Bought" value={fmtNum(totalSessionsBought)} color="#388e3c" />
          <Kpi label="Package Value" value={`₹${fmtNum(totalPackageValue)}`} color="#6366f1" />
        </div>

        {sortedDates.map(d => {
          const patients = byDate[d];
          const dateLabel = new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
          return (
            <div key={d} style={{ marginBottom: 16 }}>
              <div style={{
                fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 8,
                padding: "6px 12px", background: "#f1f5f9", borderRadius: 8,
                display: "flex", justifyContent: "space-between",
              }}>
                <span>{dateLabel}</span>
                <span style={{ color: "#3b82f6", fontWeight: 800 }}>{patients.length} enrolled</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
                {patients.map(p => {
                  const sets = p.sessionSets || [];
                  const totalQty = sets.reduce((s, ss) => s + (ss.qty || 0), 0);
                  const pending = p.pendingvisits || 0;
                  return (
                    <div key={p._id || p.patientCard} style={{
                      background: "#fff", borderRadius: 14, overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #e2e8f0",
                    }}>
                      <div style={{
                        padding: "10px 14px",
                        background: "linear-gradient(135deg,#eff6ff,#dbeafe)",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>{p.name || "—"}</div>
                          <div style={{ fontSize: 10, color: "#64748b" }}>{p.patientCard} &middot; {p.branch || ""}</div>
                        </div>
                        <div style={{
                          fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                          background: p.status === "enrolled" ? "#dcfce7" : "#fef3c7",
                          color: p.status === "enrolled" ? "#15803d" : "#92400e",
                          textTransform: "capitalize",
                        }}>{p.status}</div>
                      </div>
                      <div style={{ padding: "8px 14px" }}>
                        <div style={{ display: "flex", gap: 10, fontSize: 11, flexWrap: "wrap", marginBottom: 4 }}>
                          <span style={{ color: "#64748b" }}>Therapist: <strong style={{ color: "#1e293b" }}>{p.therapist || "—"}</strong></span>
                          <span style={{ color: "#64748b" }}>Pending: <strong style={{ color: pending > 0 ? "#16a34a" : "#dc2626" }}>{pending}</strong></span>
                        </div>
                        {sets.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            {sets.map((ss, i) => (
                              <div key={i} style={{
                                fontSize: 10, padding: "3px 8px", marginBottom: 2,
                                background: "#f8fafc", borderRadius: 6, borderLeft: "3px solid #6366f1",
                                display: "flex", justifyContent: "space-between",
                              }}>
                                <span style={{ color: "#374151", fontWeight: 600 }}>{ss.serviceName || "Sessions"} × {ss.qty || 0}</span>
                                <span style={{ color: "#6366f1", fontWeight: 700 }}>₹{fmtNum((ss.rate || 0) * (ss.qty || 0))}</span>
                              </div>
                            ))}
                            <div style={{ fontSize: 10, color: "#64748b", marginTop: 4, fontWeight: 600 }}>
                              Total: {totalQty} sessions
                            </div>
                          </div>
                        )}
                        {sets.length === 0 && (
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>No package details</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {enrollPatients.length === 0 && (
          <div className="rpt-empty">No new enrollments in this date range</div>
        )}
      </div>
    );
  }

  /* ── shared styles ──────────────────────────────────── */
  const toggleBtnStyle = (active) => ({
    padding: "6px 16px", borderRadius: 8, border: "1.5px solid", fontSize: 13, fontWeight: 700, cursor: "pointer",
    background: active ? "#1e3a5f" : "#fff", color: active ? "#fff" : "#475569",
    borderColor: active ? "#1e3a5f" : "#cbd5e1", transition: "all 0.15s",
  });

  const tabRenderers = {
    checklists:  renderChecklists,
    ott:         renderOTT,
    planner:     renderPlanner,
    attendance:  renderAttendance,
    revenue:     renderRevenue,
    enrollments: renderEnrollments,
  };

  return (
    <div className="rpt-root">
      <div className="rpt-header">
        <h2 className="rpt-title">My Reports</h2>
        <div className="rpt-date-row">
          <label>From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
          <label>To <input type="date" value={to}   onChange={e => setTo(e.target.value)} /></label>
          <button className="rpt-refresh-btn" onClick={() => fetchTab(activeTab)}>Refresh</button>
        </div>
      </div>

      <div className="rpt-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`rpt-tab${activeTab === t.id ? " active" : ""}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rpt-body">
        {loading && <div className="rpt-loading">Loading...</div>}
        {error   && <div className="rpt-error">{error}</div>}
        {!loading && !error && tabRenderers[activeTab]?.()}
      </div>
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="rpt-kpi">
      <div className="rpt-kpi-val" style={{ color: color || "#222" }}>{value}</div>
      <div className="rpt-kpi-label">{label}</div>
    </div>
  );
}
